
"use client";

import { Header } from "@/components/header";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";

import { Footer } from "@/components/footer";
import {
  Clock,
  Users,
  Star,
  Code2,
  Globe,
  Shield,
  Palette,
  BarChart3,
  Bot,
  Cpu,
  Smartphone,
} from "lucide-react";
import { ScrollReveal } from "@/components/animations/scroll-reveal";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

interface SyllabusWeek {
  week: string;
  title: string;
  topics: string[];
  tools?: string[];
  languages?: string[];
}

interface Course {
  id: string;
  title: string;
  instructor: string;
  instructorTitle: string;
  instructorImage: string;
  isProgramming: boolean;
  weeks: number;
  baseStudents: number;
  growthRate: number;
  syllabus: SyllabusWeek[];
  description: string;
  icon: React.ReactNode;
  color: string;
}

// ————————————————————————————————
// ADD YOUR COURSES HERE LATER
// ————————————————————————————————
export const syallabus: Course[] = [
  {
    id: "1",
    title: "MERN Stack Web Development",
    instructor: "Alex Chen",
    instructorTitle: "Senior Full-Stack Engineer",
    instructorImage: "/instructor-alex-chen.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 2800,
    growthRate: 110,
    color: "from-blue-500 to-cyan-500",
    icon: <Globe className="w-6 h-6" />,
    description:
      "Master the MERN Stack (MongoDB, Express, React, Node.js) in 6 months — build and deploy full-stack, production-grade applications with modern UI, APIs, and databases.",
    syllabus: [
      {
        week: "1-4",
        title: "Frontend Foundations (HTML, CSS & Tailwind)",
        topics: [
          "Introduction to HTML5 and Semantic Elements",
          "Forms, Tables, and Accessibility",
          "CSS3 Styling, Flexbox, and Grid",
          "Responsive Design with Media Queries",
          "Professional Portfolio Layout",
          "Introduction to Tailwind CSS",
          "Installation & Configuration",
          "Spacing, Sizing, Layout Utilities",
          "Flex, Grid, and Typography",
          "Transitions, Animations & Interactivity",
          "Building Responsive Landing Page Project"
        ],
        tools: ["VS Code", "Tailwind CSS", "Chrome DevTools"]
      },
      {
        week: "5-8",
        title: "JavaScript Essentials & ES6+",
        topics: [
          "Core JavaScript Concepts (Variables, Functions, Scope)",
          "DOM Manipulation & Events",
          "Arrays, Objects, and Loops",
          "Advanced ES6 Features (Destructuring, Arrow Functions)",
          "Promises, Async/Await, and Fetch API",
          "Error Handling and Debugging",
          "Local Storage & Session Storage",
          "Building Interactive Frontend Components",
          "Mini Project: Interactive Dashboard"
        ],
        tools: ["VS Code", "Postman", "Browser Console"]
      },
      {
        week: "9-12",
        title: "React JS Deep Dive",
        topics: [
          "React Fundamentals, Components, and Props",
          "State, Hooks, and Lifecycle Methods",
          "Routing with React Router",
          "Conditional Rendering and Lists",
          "Context API and State Management",
          "Form Handling and Validation",
          "API Integration and Fetching Data",
          "Advanced UI with Tailwind",
          "Project: React Blog or E-Commerce UI"
        ],
        tools: ["React DevTools", "Vite", "Tailwind CSS"]
      },
      {
        week: "13-16",
        title: "Node.js & Express Backend Development",
        topics: [
          "Intro to Node.js and NPM",
          "Modules, Events, and File System",
          "Building RESTful APIs with Express",
          "Routing, Middleware, and Controllers",
          "Error Handling & Logging",
          "Authentication (JWT) and Authorization",
          "Environment Variables & Security",
          "Mini Project: Authentication API"
        ],
        tools: ["Node.js", "Express.js", "Postman", "Nodemon"]
      },
      {
        week: "17-20",
        title: "MongoDB & Full-Stack Integration",
        topics: [
          "Introduction to NoSQL & MongoDB",
          "CRUD Operations & Data Modeling",
          "Connecting with Mongoose",
          "Database Relationships & Aggregations",
          "Building Full-Stack CRUD App (React + Node + MongoDB)",
          "API Testing and Frontend Integration",
          "Security, Validation, and Sanitization",
          "Mini Project: Task Management App"
        ],
        tools: ["MongoDB Compass", "Mongoose", "Postman"]
      },
      {
        week: "21-24",
        title: "DevOps, Deployment & Capstone Project",
        topics: [
          "Git & GitHub Workflow",
          "CI/CD with Render, Vercel, and GitHub Actions",
          "Environment Management (.env)",
          "Frontend Deployment on Vercel",
          "Backend Deployment on Render",
          "Connecting Domains & SSL Setup",
          "Capstone Project: Full MERN Web App (E-Commerce / Booking System)",
          "Testing, Debugging, Documentation, and Demo Presentation"
        ],
        tools: ["GitHub", "Render", "Vercel", "Postman", "Netlify"]
      }
    ]
  },


  {
    id: "2",
    title: "Python Full Stack Web Development",
    instructor: "Sophia Patel",
    instructorTitle: "Senior Python & Django Developer",
    instructorImage: "/instructor-sophia-patel.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 2600,
    growthRate: 105,
    color: "from-green-500 to-emerald-500",
    icon: <Globe className="w-6 h-6" />,
    description:
      "Become a professional Python Full-Stack Developer in 6 months — master frontend, backend (Django & Flask), and MySQL integration for real-world applications.",
    syllabus: [
      {
        week: "1-4",
        title: "Frontend Foundations (HTML, CSS & Tailwind)",
        topics: [
          "HTML5, Forms, Tables, and Semantics",
          "CSS3 Layout, Flexbox, and Grid",
          "Responsive Design & Animations",
          "Tailwind CSS Installation and Utilities",
          "Typography, Backgrounds, and Components",
          "Designing a Responsive Landing Page"
        ],
        tools: ["VS Code", "Tailwind CSS", "Chrome DevTools"]
      },
      {
        week: "5-8",
        title: "Python Fundamentals",
        topics: [
          "Python Basics and Syntax",
          "Data Structures and Control Flow",
          "Functions and OOP Concepts",
          "Modules, Packages, and Exception Handling",
          "Working with Files and APIs",
          "Advanced Python Features (Decorators, Generators)",
          "Intro to Data Science & ML Concepts",
          "Mini Project: CLI Data Manager"
        ],
        tools: ["Python 3", "Jupyter Notebook", "PyCharm"]
      },
      {
        week: "9-12",
        title: "Django Web Framework",
        topics: [
          "Django Setup and Configuration",
          "Models, Templates, and Views (MTV Pattern)",
          "URL Routing and Template Inheritance",
          "Django ORM & Querysets",
          "Forms, ModelForms & Authentication",
          "Static Files, Media, and Admin Customization",
          "Django REST Framework API Development",
          "Mini Project: Blog or CRM System"
        ],
        tools: ["Django", "SQLite", "Postman"]
      },
      {
        week: "13-16",
        title: "Flask & REST API Development",
        topics: [
          "Flask Setup and Routing",
          "Jinja Templates & Blueprints",
          "Connecting Flask with MySQL",
          "REST API with Flask-RESTful",
          "Authentication with JWT",
          "Error Handling & Middleware",
          "Mini Project: Notes API or Authentication System"
        ],
        tools: ["Flask", "Flask-RESTful", "Postman", "MySQL"]
      },
      {
        week: "17-20",
        title: "Database Management with MySQL",
        topics: [
          "Introduction to MySQL",
          "CRUD Commands & Queries",
          "Constraints, Joins, and Indexing",
          "Aggregate Functions & Stored Procedures",
          "Integrating Django/Flask with MySQL",
          "Data Modeling & Migration",
          "Mini Project: Inventory Management System"
        ],
        tools: ["MySQL Workbench", "DBeaver", "Django ORM"]
      },
      {
        week: "21-24",
        title: "Deployment & Capstone",
        topics: [
          "Environment Variables & Configuration",
          "Docker Basics for Python Apps",
          "Deploying Django & Flask on Render/Vercel",
          "Hosting MySQL on Cloud",
          "Final Capstone Project: Full-Stack App with MySQL",
          "Testing, Debugging, Documentation & Career Prep"
        ],
        tools: ["Render", "Vercel", "GitHub", "Docker", "MySQL Cloud"]
      }
    ]
  },


  {
    id: "3",
    title: "React Native App Development",
    instructor: "Sarah Johnson",
    instructorTitle: "Frontend Architecture Lead",
    instructorImage: "/instructor-sarah-johnson.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 1900,
    growthRate: 95,
    color: "from-purple-500 to-pink-500",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Build cross-platform Android and iOS apps using React Native — master UI design, APIs, authentication, and publishing to stores in 6 months.",
    syllabus: [
      {
        week: "1-4",
        title: "React Native Foundations",
        topics: [
          "What is React Native?",
          "Environment Setup with Expo & CLI",
          "JSX and Core Components",
          "Working with Text, Images, Buttons, and Views",
          "Flexbox Layouts & Styling",
          "Building First App & Debugging Basics",
          "Mini Project: Profile App"
        ],
        tools: ["Expo CLI", "Android Studio", "VS Code"]
      },
      {
        week: "5-8",
        title: "Components, Navigation & State",
        topics: [
          "Functional Components & Props",
          "Hooks: useState, useEffect, useContext",
          "React Navigation Setup (Stack, Tabs, Drawer)",
          "Passing Data Between Screens",
          "Redux and Context API Integration",
          "Local Storage with AsyncStorage",
          "Project: Notes or Task App"
        ],
        tools: ["React Navigation", "Redux Toolkit", "AsyncStorage"]
      },
      {
        week: "9-12",
        title: "UI/UX & Animation Mastery",
        topics: [
          "Global Styles & Reusable Components",
          "Working with Dimensions API",
          "Responsive Design & Theming",
          "Animated API and Reanimated",
          "Lottie Animations Integration",
          "Dark Mode Implementation",
          "Mini Project: Fitness Tracker App"
        ],
        tools: ["Reanimated", "Lottie", "StyleSheet API"]
      },
      {
        week: "13-16",
        title: "APIs, Firebase & Authentication",
        topics: [
          "RESTful API Integration with Fetch & Axios",
          "Firebase Setup & Firestore Database",
          "Firebase Authentication (Email/Google)",
          "Image Uploads & Cloud Storage",
          "Real-Time Data & Push Notifications",
          "Error Handling & Data Security",
          "Mini Project: Chat or Social Feed App"
        ],
        tools: ["Firebase", "Axios", "Postman"]
      },
      {
        week: "17-20",
        title: "Native Features & Advanced Concepts",
        topics: [
          "Maps, Camera, and Image Picker",
          "Push Notifications & Permissions",
          "Integrating Native Modules",
          "Offline Support & Caching",
          "App Optimization & Performance",
          "Mini Project: Location-Based App"
        ],
        tools: ["react-native-maps", "react-native-image-picker", "Expo APIs"]
      },
      {
        week: "21-24",
        title: "Testing, Deployment & Capstone",
        topics: [
          "Debugging & Profiling",
          "Unit Testing with Jest",
          "Building Signed APK & iOS IPA",
          "App Store & Play Store Publishing",
          "Version Control & OTA Updates",
          "Capstone Project: Complete Production App",
          "Portfolio Setup & Interview Prep"
        ],
        tools: ["Jest", "Expo Build", "Play Console", "App Store Connect"]
      }
    ]
  },


  {
    id: "4",
    title: "Flutter App Development",
    instructor: "David Lee",
    instructorTitle: "Mobile Solutions Architect",
    instructorImage: "/instructor-david-lee.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 2100,
    growthRate: 105,
    color: "from-sky-500 to-indigo-500",
    icon: <Smartphone className="w-6 h-6" />,
    description:
      "Master Flutter & Dart in 6 months — build production-ready Android, iOS & web apps with Firebase, REST APIs, animations, and real-world deployment.",
    syllabus: [
      {
        week: "1-4",
        title: "Dart & Flutter Fundamentals",
        topics: [
          "Introduction to Flutter & Dart",
          "Installing SDKs and IDE setup (Windows/macOS/Linux)",
          "Understanding Flutter Architecture & Widget Tree",
          "Variables, Functions, Classes, and OOP in Dart",
          "Hot Reload, Material Design Basics",
          "Building Your First Flutter App",
          "Layouts: Row, Column, Stack, and Container",
          "Styling & Themes",
          "Navigation & Routing Between Screens",
          "Mini Project: Personal Portfolio App"
        ],
        tools: ["Flutter SDK", "VS Code", "Android Studio"]
      },
      {
        week: "5-8",
        title: "UI/UX Design & Interactivity",
        topics: [
          "Working with Stateless & Stateful Widgets",
          "Form Handling & Validation",
          "Gesture Detection & Input Fields",
          "Lists, Grids & Builders",
          "Responsive UI for Different Screens",
          "Custom Widgets & Reusable Components",
          "Using Icons, Fonts & Asset Management",
          "Drawer, Bottom Navigation Bar & Tab Navigation",
          "Dark Mode Implementation",
          "Mini Project: To-Do App with Local Storage"
        ],
        tools: ["Flutter Inspector", "Dart DevTools"]
      },
      {
        week: "9-12",
        title: "Backend Integration & Data Handling",
        topics: [
          "State Management: Provider, Riverpod & Bloc Pattern",
          "Networking & RESTful APIs",
          "JSON Parsing & Models",
          "Local Database: SQLite & Hive",
          "Firebase Authentication (Email, Google, OTP)",
          "Cloud Firestore Integration",
          "Firebase Storage for Media Uploads",
          "Push Notifications with Firebase Cloud Messaging",
          "Error Handling & Data Security",
          "Project: Social Feed or Notes App with Firebase"
        ],
        tools: ["Firebase", "Postman", "Hive", "SQLite"]
      },
      {
        week: "13-16",
        title: "Advanced Flutter Features",
        topics: [
          "Google Maps & Geolocation",
          "Camera, Image Picker & File Handling",
          "Animations & Transitions (Implicit & Explicit)",
          "Custom Animations using Rive/Lottie",
          "Platform Channels (Native Android/iOS Integration)",
          "Working with Sensors & Permissions",
          "Dynamic Theming & Styling",
          "App Lifecycle & State Restoration",
          "Accessibility & Internationalization",
          "Project: Location-Based Service App"
        ],
        tools: ["Google Maps API", "Flutter Animation Controller", "Lottie"]
      },
      {
        week: "17-20",
        title: "Testing, Optimization & Deployment",
        topics: [
          "Debugging & Performance Profiling",
          "Unit, Widget & Integration Testing",
          "Handling Permissions & Errors",
          "Optimizing Build Size & Performance",
          "CI/CD with GitHub Actions & Fastlane",
          "Generating Signed APK & iOS IPA",
          "App Store & Play Store Submission",
          "Versioning & Hotfix Strategy",
          "Monetization & Ads Integration",
          "Mini Project: Publish-Ready App"
        ],
        tools: ["Flutter Analyzer", "Fastlane", "GitHub Actions", "Firebase Crashlytics"]
      },
      {
        week: "21-23",
        title: "Full-Stack Integration & Real-World Projects",
        topics: [
          "Connecting Flutter Frontend with Node.js/Django APIs",
          "Implementing Authentication (JWT/OAuth)",
          "Payment Gateway Integration (Stripe/Razorpay)",
          "Real-Time Chat with Firebase",
          "Offline Support & Caching",
          "Push Notifications & In-App Messaging",
          "Data Visualization in Flutter",
          "App Security & Encryption",
          "Team Project: Multi-User E-Commerce App"
        ],
        tools: ["Firebase", "Node.js", "Django REST", "Razorpay", "Stripe"]
      },
      {
        week: "24",
        title: "Capstone & Portfolio Building",
        topics: [
          "Final Capstone App (End-to-End)",
          "Backend Integration & Cloud Deployment",
          "UI Polish & Advanced Animations",
          "Testing, Debugging, and Documentation",
          "Publishing to Play Store & App Store",
          "Portfolio Website Setup",
          "GitHub Project Showcase",
          "Mock Interviews & Career Guidance"
        ],
        tools: ["Flutter", "Firebase", "GitHub", "Figma"]
      }
    ]
  },




  {
    id: "5",
    title: "Data Science & Machine Learning",
    instructor: "David Kumar",
    instructorTitle: "Data Science & Python Expert",
    instructorImage: "/instructor-david-kumar.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 3500,
    growthRate: 130,
    color: "from-emerald-500 to-teal-500",
    icon: <BarChart3 className="w-6 h-6" />,
    description:
      "Master Data Science and Machine Learning end-to-end — from Python and R to AI, Deep Learning, Cloud, and MLOps with real-world deployment projects.",
    syllabus: [
      {
        week: "1-4",
        title: "Python for Data Science",
        topics: [
          "Introduction to Python Programming",
          "Variables, Data Structures & Data Types",
          "Control Flow, Loops & Functions",
          "Object-Oriented Programming (OOP)",
          "Modules, Packages & Virtual Environments",
          "File & Exception Handling",
          "PEP8 & Best Practices",
          "APIs & Data Collection (JSON, CSV, REST)",
          "NumPy, Pandas, and Data Cleaning Techniques",
          "Mini Project: Data Cleaning & Analysis Pipeline"
        ],
        tools: ["Python", "Jupyter Notebook", "Google Colab", "VS Code"]
      },
      {
        week: "5-8",
        title: "R Programming & Statistics",
        topics: [
          "Introduction to R & RStudio",
          "Vectors, Lists, Matrices, Arrays, Factors",
          "Data Frames & Importing Data",
          "Control Structures & Functions",
          "Data Cleaning & Transformation in R",
          "Descriptive & Inferential Statistics",
          "Hypothesis Testing & Correlation",
          "Probability Distributions & Sampling",
          "Data Visualization with ggplot2",
          "Project: Exploratory Data Analysis with R"
        ],
        tools: ["RStudio", "ggplot2", "tidyverse"]
      },
      {
        week: "9-12",
        title: "Machine Learning Foundations",
        topics: [
          "Statistics for Data Science",
          "Data Visualization & Exploratory Data Analysis (EDA)",
          "Data Preprocessing & Feature Engineering",
          "Linear & Logistic Regression",
          "Decision Trees, Random Forests, and Ensemble Models",
          "Naive Bayes, KNN, and SVM Algorithms",
          "Dimensionality Reduction (PCA, LDA)",
          "Hyperparameter Tuning & Cross Validation",
          "Model Evaluation Metrics (Accuracy, F1, ROC-AUC)",
          "Mini Project: Predictive Modeling on Real Data"
        ],
        tools: ["Scikit-learn", "NumPy", "Pandas", "Matplotlib", "Seaborn"]
      },
      {
        week: "13-16",
        title: "Deep Learning & NLP",
        topics: [
          "Introduction to Deep Learning & Neural Networks",
          "Understanding Activation Functions & Backpropagation",
          "ANNs, CNNs, and RNNs (with TensorFlow/Keras)",
          "LSTM & GRU Models for Time Series and Text",
          "Autoencoders and Transfer Learning",
          "Hyperparameter Optimization & Regularization",
          "Natural Language Processing (NLP) Fundamentals",
          "Text Cleaning, Tokenization, POS, NER",
          "Sentiment Analysis, Topic Modeling, and Chatbots",
          "Deep Learning Project: Image or Text Classification"
        ],
        tools: ["TensorFlow", "Keras", "Scikit-learn", "NLTK", "SpaCy"]
      },
      {
        week: "17-19",
        title: "Cloud, Databases & Big Data Integration",
        topics: [
          "AWS Cloud Overview (IAM, S3, EC2, Route53)",
          "Data Storage & Retrieval using AWS S3",
          "Networking & Analytics Services Overview",
          "Introduction to MySQL & CRUD Operations",
          "Database Design, Constraints & Keys",
          "Joins, Aggregations, and Stored Procedures",
          "Database Optimization Techniques",
          "Working with BigQuery / MongoDB Basics",
          "Integrating Databases with Python & Flask",
          "Mini Project: Cloud-Connected Analytics Pipeline"
        ],
        tools: ["AWS Console", "MySQL Workbench", "BigQuery", "FastAPI"]
      },
      {
        week: "20-21",
        title: "Data Visualization & Business Intelligence",
        topics: [
          "Tableau Interface & Data Connection",
          "Charts, Maps, and Dashboards",
          "Table Calculations & Storytelling",
          "Advanced Data Preparation and Blending",
          "Power BI Desktop Setup",
          "Data Modeling & Relationships in Power BI",
          "DAX Calculations and AI Visuals",
          "Publishing Reports and Collaboration",
          "Data Storytelling for Business Decisions",
          "Visualization Project: Interactive BI Dashboard"
        ],
        tools: ["Tableau", "Power BI"]
      },
      {
        week: "22-23",
        title: "MLOps & Deployment",
        topics: [
          "Introduction to MLOps & Lifecycle",
          "Model Serving using FastAPI",
          "Docker Containerization & Image Building",
          "Streamlit for Interactive Dashboards",
          "Version Control & CI/CD Pipelines",
          "Model Monitoring & Retraining",
          "Deployment on Heroku, AWS, or Render",
          "Automating ML Pipelines",
          "Testing and Model Validation",
          "Mini Project: End-to-End Model Deployment"
        ],
        tools: ["Docker", "Heroku", "Streamlit", "GitHub Actions", "FastAPI"]
      },
      {
        week: "24",
        title: "Capstone Project & Career Preparation",
        topics: [
          "End-to-End Data Science Capstone",
          "Data Collection, Cleaning & Feature Engineering",
          "Model Building, Evaluation, and Deployment",
          "Cloud Hosting and Dashboard Presentation",
          "Documentation & Reporting",
          "Portfolio & GitHub Setup",
          "Mock Interviews & Resume Building",
          "Presentation with Instructors and Mentors"
        ],
        tools: [
          "Python",
          "SQL",
          "Tableau",
          "Power BI",
          "Docker",
          "Streamlit",
          "AWS"
        ]
      }
    ]
  },


  {
    id: "6",
    title: "Data Analytics",
    instructor: "Rachel Green",
    instructorTitle: "Full-Stack & Entrepreneurship Coach",
    instructorImage: "/instructor-rachel-green.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 4100,
    growthRate: 120,
    color: "from-amber-500 to-orange-500",
    icon: <BarChart3 className="w-6 h-6" />,
    description:
      "Master end-to-end data analytics — from Python, SQL, and Excel to Tableau and Power BI. Build real-world dashboards and data-driven business insights.",
    syllabus: [
      {
        week: "1-4",
        title: "Python for Data Analytics",
        topics: [
          "Introduction to Python and Data Analytics",
          "String Manipulation and Data Structures",
          "Control Loops and Functions",
          "Object-Oriented Programming",
          "Modules and Packages",
          "Graphical User Interface (GUI)",
          "Exception & File Handling",
          "Variables, Scope, and PEP8 Standards",
          "Advanced Concepts and Error Handling",
          "Working with APIs and JSON Data",
          "Mini Project: Data Cleaning with Python"
        ],
        tools: ["Python", "Jupyter Notebook", "Google Colab"]
      },
      {
        week: "5-8",
        title: "Advanced Excel for Analytics",
        topics: [
          "Excel Interface and Data Entry",
          "Formulas & Functions for Analysis",
          "Lookup & Reference Functions (VLOOKUP, INDEX, MATCH)",
          "String Functions and Conditional Formatting",
          "Pivot Tables, Pivot Charts, and Dashboards",
          "Aggregate Functions and Data Validation",
          "Scenario Manager & What-If Analysis",
          "Macros, VBA Basics & Automation",
          "Excel Data Cleaning & Transformation",
          "Industry-Oriented Excel Analytics Project"
        ],
        tools: ["Microsoft Excel"]
      },
      {
        week: "9-12",
        title: "SQL for Data Management & Analysis",
        topics: [
          "Introduction to MySQL & Database Basics",
          "Creating Databases and Tables",
          "Data Insertion and Updates",
          "MySQL Warnings, NULL and NOT NULL",
          "CRUD Commands: SELECT, UPDATE, DELETE, INSERT",
          "WHERE, AND, OR, DISTINCT, IN/NOT IN Clauses",
          "String Functions: SUBSTRING, REPLACE, REVERSE, CHAR_LENGTH",
          "Case Manipulation (UPPER, LOWER)",
          "Relational Database Concepts and Schemas",
          "Primary, Foreign & Unique Keys, Constraints",
          "Aggregate Functions and Joins",
          "Stored Procedures and Parameters",
          "Control Statements: IF, CASE, LOOP, WHILE",
          "Access Control for Stored Programs",
          "SQL Optimization & Query Debugging",
          "Industry Projects using MySQL and PostgreSQL"
        ],
        tools: ["MySQL", "PostgreSQL", "DBeaver"]
      },
      {
        week: "13-16",
        title: "Tableau Data Visualization",
        topics: [
          "Getting Started with Tableau & Interface Overview",
          "Connecting to Data Sources (Excel, CSV, SQL)",
          "Data Preparation and Cleaning in Tableau",
          "Creating Charts, Maps, and Visual Elements",
          "Scatterplots, Dual Axis Charts, and Heatmaps",
          "Table Calculations and Aggregations",
          "Advanced Dashboards and Interactivity",
          "Storytelling and Presentation Design",
          "Joining & Blending Multiple Data Sources",
          "Parameters and Calculated Fields",
          "Publishing Dashboards to Tableau Public",
          "Team Collaboration and Real-Time Updates"
        ],
        tools: ["Tableau Desktop", "Tableau Public"]
      },
      {
        week: "17-20",
        title: "Power BI for Business Insights",
        topics: [
          "Introduction to Power BI & Interface Setup",
          "Data Import and Cleaning (Power Query)",
          "Creating Relationships and Data Models",
          "Data Analysis Expressions (DAX)",
          "Aggregations, Filters, and Calculations",
          "Creating Dynamic Visuals and Reports",
          "Drill-Through and Cross-Filtering",
          "Integrating Power BI with Excel and SQL",
          "Implementing AI Visuals in Reports",
          "Publishing & Sharing Reports on Power BI Service",
          "Scheduled Data Refresh and Workspace Management",
          "Power BI Project: Business Sales Dashboard"
        ],
        tools: ["Power BI Desktop", "Power BI Service"]
      },
      {
        week: "21-22",
        title: "Foundations of Data Science & Machine Learning",
        topics: [
          "Introduction to Data Science Concepts",
          "Understanding Data Pipelines",
          "Exploratory Data Analysis (EDA)",
          "Data Cleaning and Preprocessing",
          "Introduction to Machine Learning",
          "Supervised vs Unsupervised Learning",
          "Linear and Logistic Regression",
          "Decision Trees and Model Evaluation",
          "Deploying Simple Models using Streamlit or Flask"
        ],
        tools: ["Python", "Scikit-learn", "Pandas", "NumPy"]
      },
      {
        week: "23-24",
        title: "Capstone Project & Industry Applications",
        topics: [
          "Real-World Data Analytics Case Study",
          "Combining Python, SQL, Tableau & Power BI",
          "Data Cleaning and ETL Pipeline Design",
          "Building a Full Interactive Dashboard",
          "Performing Business Analysis and Insights",
          "Report Creation and Storytelling with Data",
          "Presentation & Review with Mentors",
          "Portfolio Building and Job Preparation"
        ],
        tools: [
          "Python",
          "Excel",
          "SQL",
          "Tableau",
          "Power BI",
          "Streamlit"
        ]
      }
    ]
  },

  {
    id: "7",
    title: "UX/UI Designing & Product Prototyping",
    instructor: "Mike Brown",
    instructorTitle: "UX/UI & Web Design Lead",
    instructorImage: "/instructor-mike-brown.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 5200,
    growthRate: 140,
    color: "from-rose-500 to-pink-500",
    icon: <Palette className="w-6 h-6" />,
    description:
      "Master UX/UI Design with a human-centered approach — learn research, wireframing, prototyping, design systems, usability testing, and build a professional portfolio using Figma, Adobe XD, and industry tools.",
    syllabus: [
      {
        week: "1-4",
        title: "Introduction to UX & Design Thinking",
        topics: [
          "Clearing misconceptions about design",
          "Understanding design as a problem-solving process",
          "Introduction to User-Centered Design (UCD)",
          "Stanford Design School’s Design Thinking Methodology",
          "Heuristic Evaluation and Usability Principles",
          "Quantitative vs Qualitative Data",
          "Empathize, Define, Ideate, Prototype, Test Framework",
          "Solution-based Approach to Problems"
        ],
        tools: ["Miro", "Notion", "Figma"]
      },
      {
        week: "5-8",
        title: "User Research & Empathy Mapping",
        topics: [
          "Empathy Methods for Conducting Research",
          "Beginner’s Mindset and Interview Techniques",
          "What–How–Why Analysis",
          "Photo/Video Diary Studies",
          "Journey Maps & Story Capturing",
          "Affinity Mapping & Persona Development",
          "POV Statements and HMW Questions",
          "User Stories, Scenarios & Use Cases"
        ],
        tools: ["Miro", "FigJam", "Google Forms", "Figma"]
      },
      {
        week: "9-12",
        title: "Ideation, Information Architecture & Wireframing",
        topics: [
          "Ideation Techniques (Brainstorming, Brainwriting, Braindumping)",
          "Post-it Voting, Four Categories Method, Now-Wow-How Matrix",
          "Information Architecture & User Flows",
          "Mental Models & Sitemap Creation",
          "Wireframing Concepts & Lo-Fi Prototypes",
          "Usability Heuristics in Wireframes",
          "Practical Wireframing Exercises",
          "Mini Project: Redesign an Existing App Experience"
        ],
        tools: ["Figma", "Adobe XD", "Balsamiq", "Lucidchart"]
      },
      {
        week: "13-16",
        title: "UI Design & Material Design Systems",
        topics: [
          "UI Fundamentals: Design Principles & Elements",
          "Color Theory & Contrast Ratios",
          "Typography, Grids, and Layout Systems",
          "Material Design & iOS Guidelines",
          "Creating Design Systems and Components",
          "White and Dark Theme Design",
          "Accessibility in Design (WCAG Standards)",
          "Mini Project: Style Guide + Component Library"
        ],
        tools: ["Figma", "Adobe XD", "Figma Plugins"]
      },
      {
        week: "17-20",
        title: "Prototyping, Testing & Feedback",
        topics: [
          "Prototyping Concepts & Types (Low-Fi, Mid-Fi, Hi-Fi)",
          "Paper Prototyping & Interactive Prototypes",
          "Storytelling Through Design",
          "User Testing & Usability Studies",
          "Feedback Capture Grid & Remote Testing",
          "A/B Testing and Analytics for UX Validation",
          "Iteration Based on User Feedback",
          "Mini Project: Test & Improve Your App Prototype"
        ],
        tools: ["Figma", "InVision", "Marvel", "Maze", "Lookback.io"]
      },
      {
        week: "21-24",
        title: "Responsive Design, Portfolio & Career Prep",
        topics: [
          "Responsive Design Principles (Web & Mobile)",
          "Adapting Layouts Across Devices",
          "Design Handoff for Developers",
          "Case Study Documentation",
          "Portfolio Presentation (Behance & Dribbble)",
          "Building Freelance & Agency-Ready Projects",
          "Final Capstone Project: Complete Product Redesign",
          "Mock Interview & Design Challenge Practice"
        ],
        tools: ["Figma", "Behance", "Canva", "Notion"]
      }
    ]
  },
  {
    id: "8",
    title: "Digital Marketing (6-Month Program)",
    instructor: "Rachel Green",
    instructorTitle: "Full-Stack & Entrepreneurship Coach",
    instructorImage: "/instructor-rachel-green.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 6700,
    growthRate: 160,
    color: "from-indigo-500 to-purple-500",
    icon: <Globe className="w-6 h-6" />,
    description:
      "Master SEO, Google Ads, Social Media, and Email Marketing to grow any business with real-world tools and live projects.",
    syllabus: [
      {
        week: "1",
        title: "Introduction to SEO",
        topics: [
          "How a Search Engine works",
          "Factors affecting website rankings",
          "Four dimensions of SEO",
        ],
        tools: ["Google Search Console", "Google Analytics"],
      },
      {
        week: "2",
        title: "Keyword Research & On-Page Optimization",
        topics: [
          "Keyword Research and Competitive Analysis",
          "Short-tail vs Long-tail keywords",
          "On-Page Optimization: Meta Tags, Headings, Content",
          "Internal Linking, Sitemap Creation",
        ],
        tools: ["Google Keyword Planner", "SEMRush", "Ahrefs", "Ubersuggest"],
      },
      {
        week: "3",
        title: "Off-Page SEO, Site Architecture & Technical SEO",
        topics: [
          "Backlink Strategies and Google Page Rank",
          "Directory & Article Submissions",
          "Website Taxonomy, Internal Linking, Robots.txt",
          "Local SEO Optimization",
        ],
        tools: ["Google My Business", "Screaming Frog", "Moz Local"],
      },
      {
        week: "4",
        title: "SEO Audits & Algorithm Updates",
        topics: [
          "Conducting Site Audits",
          "Crawl, Sitemap, and Speed Optimization",
          "Panda, Penguin, Hummingbird, and Mobile Updates",
          "SEO Tracking and Performance Measurement",
        ],
        tools: ["Google PageSpeed Insights", "Lighthouse", "GTmetrix"],
      },
      {
        week: "5",
        title: "Google Ads (Search & Display)",
        topics: [
          "Creating and Setting Up Google Ads Account",
          "Campaign and Ad Group Structure",
          "Writing High-Converting Ads",
          "Keyword Bidding and Quality Score",
          "Conversion Tracking and ROI Measurement",
          "Using AdWords Scripts for Optimization",
          "Live Projects",
        ],
        tools: ["Google Ads", "Optmyzr", "Tag Manager"],
      },
      {
        week: "6",
        title: "Facebook, Instagram & Social Media Marketing",
        topics: [
          "Facebook Page Setup and Optimization",
          "Facebook Ads, Pixel, and Remarketing",
          "Business Manager and Ad Agency Setup",
          "Instagram Marketing Strategy and Monetization",
          "Instagram Stories, IGTV, and Ads",
          "Social Media Scheduling and Reporting",
          "Live Projects & Final Assessment",
        ],
        tools: [
          "Meta Business Suite",
          "Canva",
          "Mailchimp",
          "Hootsuite",
          "Buffer",
          "Later",
        ],
      },
    ],
  },



  {
    id: "9",
    title: "Software Testing (6-Month Program)",
    instructor: "Emma Wilson",
    instructorTitle: "TypeScript & DevOps Specialist",
    instructorImage: "/instructor-emma-wilson.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 2100,
    growthRate: 85,
    color: "from-gray-600 to-slate-600",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Master Manual & Automation Testing with Core Java, Selenium WebDriver, JUnit, Maven, and CI/CD tools.",
    syllabus: [
      {
        week: "1",
        title: "Core Java for Test Automation",
        topics: [
          "Java Program Structure and Syntax",
          "Classes and Objects",
          "Data Types and Variables",
          "Access Modifiers and Functions",
          "Loops and Conditional Statements",
          "OOP Concepts: Abstraction, Polymorphism, Inheritance, Encapsulation",
          "Exception Handling in Java",
          "Collections Framework (List, Set, Map)",
        ],
        tools: ["Java", "IntelliJ IDEA", "Eclipse"],
      },
      {
        week: "2",
        title: "Locators and Selenium Basics",
        topics: [
          "Understanding Locators and Their Types",
          "Using Locators with Practical Examples",
          "Advantages and Limitations of Locators",
          "Introduction to Selenium",
          "Selenium IDE & WebDriver Overview",
          "Selenium Components and Evolution",
          "Environment Setup: JDK, JAVA PATH, JAR Files",
          "Writing and Running Basic Java + Selenium Scripts",
        ],
        tools: ["Selenium IDE", "Eclipse", "ChromeDriver"],
      },
      {
        week: "3",
        title: "Selenium WebDriver Advanced",
        topics: [
          "Setting Up WebDriver for Multiple Browsers",
          "Using Identifiers in WebDriver",
          "Creating WebDriver Instances (Chrome, Firefox, Edge, IE)",
          "Handling WebElements and Synchronization Issues",
          "Dynamic Object Handling and AJAX Controls",
          "Working with WebTables and Select Class",
          "Performing Advanced Actions using Action Class",
        ],
        tools: ["Selenium WebDriver", "TestNG", "ChromeDriver", "GeckoDriver"],
      },
      {
        week: "4",
        title: "Selenium Testing Frameworks & Reporting",
        topics: [
          "Setting Up Cross-Browser Tests (IE, Chrome)",
          "Creating End-to-End Test Scenarios",
          "JUnit Framework: Setup, Annotations, and Execution",
          "Generating JUnit Reports",
          "TestNG Framework Introduction (Optional)",
          "Assertions and Test Suites",
          "Integrating Selenium with TestNG/JUnit",
        ],
        tools: ["JUnit", "TestNG", "Allure Reports"],
      },
      {
        week: "5",
        title: "Database & Maven Integration",
        topics: [
          "Connecting Java to Databases (JDBC)",
          "Executing Select and Update Queries via Code",
          "Looping Through Query Results",
          "Introduction to Maven",
          "Advantages and Project Setup in Maven",
          "Adding Dependencies in pom.xml",
          "Integrating Selenium and TestNG with Maven",
        ],
        tools: ["MySQL", "PostgreSQL", "JDBC", "Maven"],
      },
      {
        week: "6",
        title: "Automation, CI/CD & Final Project",
        topics: [
          "Automation Framework Design (Hybrid/Keyword Driven)",
          "Running Automated Tests in Jenkins",
          "Generating Allure and Extent Reports",
          "Version Control with Git and GitHub",
          "Continuous Integration Setup (Jenkins Pipeline)",
          "Working on Real-Time Projects",
          "Mock Interviews and Resume Building",
          "Final Project Submission & Review",
        ],
        tools: ["Jenkins", "GitHub", "Allure Reports", "Postman", "Cypress"],
      },
    ],
  },

  {
    id: "10",
    title: "Graphic Designing (6-Month Program)",
    instructor: "Mike Brown",
    instructorTitle: "UX/UI & Web Design Lead",
    instructorImage: "/instructor-mike-brown.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 4400,
    growthRate: 125,
    color: "from-fuchsia-500 to-purple-500",
    icon: <Palette className="w-6 h-6" />,
    description:
      "Master Photoshop, Illustrator, InDesign, and UX/UI Design — build creative visuals, branding, and digital experiences for web and print.",
    syllabus: [
      {
        week: "1-4",
        title: "Adobe Photoshop – Fundamentals to Advanced",
        topics: [
          "Introduction & Interface",
          "Color & Adjustment Layers",
          "Shapes and Text Design",
          "Layer Styles and Effects",
          "Cropping and Composition",
          "Selections & Masking Techniques",
          "Filters and Retouching",
          "Transform & Distort Tools",
          "Blending Modes and Shadows",
          "Visual Styles & Artboards",
          "Color Modes & Resolution",
          "Using Brushes and Effects",
        ],
        tools: ["Adobe Photoshop"],
      },
      {
        week: "5-8",
        title: "Adobe Illustrator – Vector Design & Logo Creation",
        topics: [
          "Illustrator Basics and Interface",
          "Working with Objects and Paths",
          "Object Appearance and Styles",
          "Drawing & Custom Brushes",
          "Using and Editing Images",
          "Typography and Type Tools",
          "Advanced Techniques and Effects",
          "Exporting and Integration with Other Adobe Apps",
          "CC New Features Overview",
          "Professional Vector Projects (Logo, Branding, Icons)",
        ],
        tools: ["Adobe Illustrator"],
      },
      {
        week: "9-12",
        title: "Adobe InDesign – Print & Layout Design",
        topics: [
          "Introduction to InDesign",
          "Document Setup and Navigation",
          "Working with Text and Paragraph Styles",
          "Placing and Managing Images",
          "Grids, Guides, and Margins",
          "Design Fundamentals for Print",
          "Creating Brochures, Magazines, and Flyers",
          "Master Pages and Templates",
          "Preparing for Print & Exporting PDFs",
          "Professional Publication Project",
        ],
        tools: ["Adobe InDesign"],
      },
      {
        week: "13-18",
        title: "UX/UI Design – User Experience and Interface Design",
        topics: [
          "Introduction to UX/UI and Design Thinking Process",
          "Empathize Phase – Research & Deliverables",
          "Define & Ideate Phases – User Flow & Wireframes",
          "Learning UI Tools & Material Design Principles",
          "Prototyping & Testing",
          "Design Fundamentals for Digital Products",
          "Responsive Design for Web and Mobile",
          "Creating Interactive Prototypes",
          "Design Systems & Accessibility",
          "Professional UX/UI Project",
        ],
        tools: ["Figma", "Adobe XD", "Miro"],
      },
      {
        week: "19-24",
        title: "Professional Projects & Portfolio Development",
        topics: [
          "Designing for Multiple Screens (Web, Mobile, Tablet)",
          "Responsive Web & Mobile Projects",
          "Brand Identity and Design Systems",
          "Mockups and Client Presentation Techniques",
          "Final Design Project Execution",
          "Portfolio Curation and Presentation",
          "Exporting and Showcasing Work on Behance",
          "Freelancing and Industry Readiness",
        ],
        tools: ["Figma", "Canva", "Behance", "Adobe Creative Suite"],
      },
    ],
  },

  {
    id: "11",
    title: "Cyber Security (6-Month Program)",
    instructor: "Lisa Anderson",
    instructorTitle: "Backend & Database Expert",
    instructorImage: "/instructor-lisa-anderson.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 1800,
    growthRate: 90,
    color: "from-red-600 to-rose-600",
    icon: <Shield className="w-6 h-6" />,
    description:
      "Ethical hacking, penetration testing, secure coding and defensive best practices — learn hands-on techniques in lab environments and prepare for real-world assessments and certifications.",
    syllabus: [
      {
        week: "1",
        title: "Introduction & Ethics",
        topics: [
          "Hacking concepts and ethical hacking overview",
          "Difference: hacking vs ethical hacking",
          "Phases of a security assessment (reconnaissance → exploitation → post-exploit → reporting)",
          "Types of hackers and attacker motivations",
          "Types of operating systems used in security research",
          "Legal, ethical and safe lab practices (conducting tests in controlled environments)",
        ],
        tools: ["Documentation", "Lab VMs"],
      },
      {
        week: "2",
        title: "Getting Started with Kali Linux & Alternative OSes",
        topics: [
          "Downloading and verifying Kali ISO images",
          "Creating bootable USB/DVD and installing Kali",
          "Running Kali in VirtualBox and VMware (live mode vs VM vs dual-boot)",
          "Overview of alternative security distros (Parrot, BlackArch, Garuda, etc.)",
          "Safe setup of lab environments and snapshot workflows",
        ],
        tools: ["Kali Linux", "VirtualBox", "VMware"],
      },
      {
        week: "3",
        title: "Linux Filesystem, OS Internals & OpSec",
        topics: [
          "Linux filesystem hierarchy (/bin, /boot, /etc, /home, /proc, /sys, /usr, /var, etc.)",
          "Basic Linux administration commands and navigation",
          "User and permissions model",
          "Operational security (OpSec) and threat intelligence basics",
          "Malicious file analysis (concepts) and safe tooling",
        ],
        tools: ["Kali Terminal", "Wireshark (intro)"],
      },
      {
        week: "4",
        title: "OSINT & Social Engineering (Defensive Focus)",
        topics: [
          "Open-Source Intelligence (OSINT) methodology and data sources",
          "Reconnaissance: usernames, domains, IPs, images, social networks",
          "Search engines, people search, public records, geolocation tools",
          "Metadata, language translation, archived content",
          "Social engineering types: phishing, vishing, spear-phishing, whaling",
          "Defensive controls and awareness training to mitigate social engineering",
        ],
        tools: ["OSINT toolkits (theory)", "Safe demo datasets"],
      },
      {
        week: "5",
        title: "Web Vulnerabilities & Defensive Testing",
        topics: [
          "Web vulnerability fundamentals (SQLi, XSS, CSRF) — concept, impact and mitigation",
          "Introduction to web security testing workflows (safe, non-destructive testing in lab environments)",
          "Using proxy tools and scanners responsibly (Burp Suite basics — proxy, intercept, scanner concepts)",
          "Secure coding best practices to prevent common web flaws",
          "Directory and content discovery, request/response analysis",
        ],
        tools: ["Burp Suite", "DVWA (lab)"],
      },
      {
        week: "6",
        title: "System Hacking, Exploitation Concepts & Certification Prep",
        topics: [
          "System security concepts and common attack vectors (privilege escalation concepts, password attacks) — defensive perspective",
          "Overview of exploitation frameworks and responsible use (Metasploit high-level concepts only)",
          "Password cracking concepts and hardening strategies (what defenders should know)",
          "Pharming, URL manipulation, and web security precautions",
          "Conducting safe capture-the-flag (CTF) exercises and reporting findings",
          "Writing professional security assessment reports and CEH/other cert prep guidance",
          "Final capstone: end-to-end security assessment in a controlled lab, remediation plan and presentation",
        ],
        tools: [
          "Metasploit (conceptual)",
          "John the Ripper (conceptual)",
          "TryHackMe / HackTheBox (lab platforms)",
          "Wireshark",
          "Reporting templates",
        ],
      }
    ],
  },



  {
    id: "12",
    title: "3D Software Architect (6-Month Program)",
    instructor: "James Lee",
    instructorTitle: "Vue.js Framework Expert",
    instructorImage: "/instructor-james-lee.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 800,
    growthRate: 70,
    color: "from-violet-600 to-indigo-600",
    icon: <Cpu className="w-6 h-6" />,
    description:
      "Master the principles of 3D design, system organization, and architectural visualization using SketchUp. Learn modeling, textures, materials, animation, and terrain creation for real-world design projects.",
    syllabus: [
      {
        week: "1",
        title: "SketchUp Interface & Fundamentals",
        topics: [
          "Introduction to SketchUp interface and workspace setup",
          "Navigating the 3D environment and camera views",
          "Walking around, orbiting, panning, and zooming",
          "Creating shadows, fog, and lighting perspectives",
          "Creating and managing scenes for presentations",
          "Selecting, moving, scaling, and rotating objects",
          "Manipulating faces, edges, and geometry",
          "Using advanced selection tools efficiently",
        ],
        tools: ["SketchUp"],
      },
      {
        week: "2",
        title: "Drawing & Modeling in 3D",
        topics: [
          "Line tool fundamentals and 3D line drawing",
          "Using rectangles, circles, polygons, and arcs",
          "Extruding shapes with Push/Pull tool",
          "Offset and Follow Me tools for advanced modeling",
          "Creating text and annotations in 3D",
          "Softening and smoothing round edges",
          "Using guides and construction tools for precision",
          "Creating cross sections and cutaways",
        ],
        tools: ["SketchUp", "Layout"],
      },
      {
        week: "3",
        title: "Scene Organization & Components",
        topics: [
          "Grouping objects and creating reusable components",
          "Managing and organizing models with layers/tags",
          "Using the Outliner for complex scene hierarchy",
          "Hiding, unhiding, locking, and unlocking geometry",
          "Organizing large architectural projects efficiently",
          "Model optimization and cleanup best practices",
        ],
        tools: ["SketchUp Pro"],
      },
      {
        week: "4",
        title: "Importing & Integrations",
        topics: [
          "Importing 3D models from Google Warehouse (3D Warehouse)",
          "Importing and managing landscape components",
          "Integrating SketchUp models with Google Earth",
          "Working with Dynamic Components and their parameters",
          "Creating interactive architectural objects",
        ],
        tools: ["3D Warehouse", "Google Earth"],
      },
      {
        week: "5",
        title: "Textures, Materials & Mapping",
        topics: [
          "Applying and editing materials in SketchUp",
          "Creating custom materials and textures",
          "Mapping images on surfaces and curved geometries",
          "Projecting bitmap images for realistic visualization",
          "Handling transparency and reflectivity in designs",
          "Rendering workflows for presentation-ready models",
        ],
        tools: ["SketchUp", "V-Ray (optional)", "Adobe Photoshop"],
      },
      {
        week: "6",
        title: "Styles, Animation & Terrain Creation",
        topics: [
          "Applying and customizing visual styles",
          "Creating and saving custom style templates",
          "Animating scenes and camera paths",
          "Creating smooth scene transitions and walkthroughs",
          "Using the Sandbox tool to create terrain",
          "Generating contour lines and surface topology",
          "Sculpting terrain with the Smoove tool",
          "Stamping and draping buildings or roads on terrain",
          "Final project: complete architectural environment design",
        ],
        tools: ["SketchUp", "Sandbox Tools"],
      },
    ],
  },

  {
    id: "13",
    title: "JAVA (6-Month Program)",
    instructor: "Alex Chen",
    instructorTitle: "Senior Full-Stack Engineer",
    instructorImage: "/instructor-alex-chen.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 3900,
    growthRate: 115,
    color: "from-orange-500 to-red-500",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Comprehensive Core Java program covering OOP concepts, Exception Handling, Collections, Multithreading, AWT, and advanced Java essentials for backend and enterprise development.",
    syllabus: [
      {
        week: "1",
        title: "Introduction to Java",
        topics: [
          "Overview of Java and its platform independence",
          "Object-Oriented Principles in Java",
          "Automatic Memory Management (Garbage Collection)",
          "Compiled and Interpreted Nature of Java",
          "Robust and Secure features of Java",
          "Dynamic Linking and Multithreading Overview",
          "Java Networking Basics",
        ],
        tools: ["JDK", "IntelliJ IDEA", "VS Code"],
      },
      {
        week: "2",
        title: "Java Fundamentals",
        topics: [
          "Data Types, Variables, and Operators",
          "Control Statements (if, switch, loops)",
          "Arrays and Enhanced For-Loop",
          "Enumerations and Static Import",
          "Auto Boxing and Unboxing",
          "C-style Formatted I/O",
          "Variable Arguments and Overloading",
        ],
        tools: ["IntelliJ IDEA", "Eclipse"],
      },
      {
        week: "3",
        title: "Essentials of Object-Oriented Programming",
        topics: [
          "Object and Class Definitions",
          "Encapsulation and Access Modifiers",
          "Inheritance and Method Overriding",
          "Polymorphism and Dynamic Binding",
          "Constructors and Object Lifecycle",
          "Using Interfaces and Abstract Classes",
          "Understanding Classpath and Packages",
        ],
        tools: ["IntelliJ IDEA", "Maven"],
      },
      {
        week: "4",
        title: "Advanced Java Programming",
        topics: [
          "Exception Handling Mechanisms",
          "Types of Exceptions and Propagation",
          "Using try, catch, throw, throws, finally",
          "Creating User-Defined Exceptions",
          "I/O Operations: File Handling, Streams, Readers/Writers",
          "Java Util Package & Collections Framework",
          "List, Set, Map, Comparator, Hashtable, Properties",
        ],
        tools: ["IntelliJ IDEA", "JDK", "Maven"],
      },
      {
        week: "5",
        title: "Multithreading & Generics",
        topics: [
          "Introduction to Multithreading",
          "Thread Lifecycle and Java Thread Model",
          "Thread Class and Runnable Interface",
          "Thread Synchronization and Communication",
          "Preventing Deadlocks and Managing Priorities",
          "Introduction to Generics and Type Safety",
          "Creating and Using Generic Classes",
          "Bounded and Wildcard Generics",
        ],
        tools: ["IntelliJ IDEA"],
      },
      {
        week: "6",
        title: "Inner Classes, AWT, and Advanced Topics",
        topics: [
          "Inner Classes – Nested, Member, Local, and Anonymous",
          "Abstract Window Toolkit (AWT) Overview",
          "Graphics, Colors, and Fonts",
          "AWT Components, Event Handling, and Layout Managers",
          "Advanced Collections – Comparator, Iterable, Streams",
          "Mini Project: Java Desktop App using AWT and Threads",
        ],
        tools: ["IntelliJ IDEA", "AWT", "Swing (optional)"],
      },
    ],
  },
  {
    id: "14",
    title: "Advance Data Structure",
    instructor: "David Kumar",
    instructorTitle: "Data Science & Python Expert",
    instructorImage: "/instructor-david-kumar.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 3100,
    growthRate: 105,
    color: "from-slate-600 to-zinc-600",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Master Data Structures and Algorithms with hands-on implementation in C, C++, Java, Python, and JavaScript. Learn problem-solving patterns used in top coding interviews and competitive programming.",
    syllabus: [
      {
        week: "1",
        title: "Programming Foundations & OOP",
        topics: [
          "Introduction to Programming Logic and Syntax",
          "Data Types, Variables, and Operators",
          "Control Statements and Loops",
          "Functions and Recursion",
          "Arrays and Strings Basics",
          "Pointers and Memory Management (C/C++)",
          "Introduction to Object-Oriented Programming",
          "Classes, Objects, Constructors, and Inheritance",
        ],
        languages: ["C", "C++", "Java", "Python", "JavaScript"],
        tools: ["VS Code", "GCC", "Code::Blocks", "OnlineGDB"],
      },
      {
        week: "2",
        title: "Core Data Structures – Linear",
        topics: [
          "Arrays – Static & Dynamic Memory",
          "Linked Lists – Singly, Doubly, and Circular",
          "Stacks – Implementation using Arrays & Linked Lists",
          "Queues – Simple, Circular, and Priority Queues",
          "Deques and Custom Implementations",
          "String Manipulation & Pattern Matching",
          "Complexity Analysis – Time and Space",
        ],
        languages: ["C", "C++", "Java", "Python", "JavaScript"],
        tools: ["LeetCode", "HackerRank", "GeeksforGeeks"],
      },
      {
        week: "3",
        title: "Non-Linear Data Structures",
        topics: [
          "Introduction to Trees and Binary Trees",
          "Binary Search Trees (BST)",
          "Tree Traversal – Inorder, Preorder, Postorder",
          "AVL Trees, Red-Black Trees, and Heaps",
          "Priority Queues using Heap",
          "Graphs – Representation (Adjacency List & Matrix)",
          "Graph Traversal – BFS, DFS",
          "Topological Sorting and Cycle Detection",
        ],
        languages: ["C++", "Java", "Python"],
        tools: ["Visualgo", "Codeforces", "LeetCode"],
      },
      {
        week: "4",
        title: "Hashing & Advanced DSA",
        topics: [
          "Hash Tables – Implementation and Collisions",
          "Chaining and Open Addressing",
          "Tries – Prefix Trees",
          "Union-Find (Disjoint Set Union)",
          "Segment Trees and Fenwick Trees (BIT)",
          "Matrix-based Problems and Search Algorithms",
          "Practical DSA Design Patterns",
        ],
        languages: ["C++", "Java", "Python"],
        tools: ["HackerEarth", "InterviewBit"],
      },
      {
        week: "5",
        title: "Algorithms & Problem Solving",
        topics: [
          "Sorting Algorithms – Quick, Merge, Heap, Counting, Radix",
          "Searching Algorithms – Binary, Linear, Exponential",
          "Divide & Conquer Paradigm",
          "Dynamic Programming Fundamentals",
          "Memoization vs Tabulation",
          "Greedy Algorithms and Optimization Problems",
          "Backtracking (N-Queens, Sudoku, Subset Sum)",
          "Bit Manipulation Techniques",
        ],
        languages: ["C++", "Java", "Python", "JavaScript"],
        tools: ["LeetCode", "CodeChef", "AtCoder"],
      },
      {
        week: "6",
        title: "Competitive Programming & Interview Prep",
        topics: [
          "Algorithmic Thinking and Problem Decomposition",
          "Sliding Window, Two Pointer, and Recursion Patterns",
          "Graph Algorithms – Dijkstra, Floyd-Warshall, MST (Kruskal/Prim)",
          "String Algorithms – KMP, Rabin Karp, Z-Algorithm",
          "System Design Thinking for Large Data Problems",
          "Mock Tests and Interview Simulation",
          "Capstone Project: Build DSA Visualizer or Problem Tracker",
        ],
        languages: ["C++", "Java", "Python", "JavaScript"],
        tools: ["LeetCode", "HackerRank", "Codeforces", "GitHub"],
      },
    ],
  }
  ,
  {
    id: "15",
    title: "RPA Training (UiPath & Automation Anywhere)",
    instructor: "Lisa Anderson",
    instructorTitle: "Backend & Database Expert",
    instructorImage: "/instructor-lisa-anderson.jpg",
    isProgramming: false,
    weeks: 8,
    baseStudents: 5600,
    growthRate: 150,
    color: "from-lime-500 to-green-500",
    icon: <Bot className="w-6 h-6" />,
    description: "UiPath & Automation Anywhere – Automate repetitive business tasks efficiently.",
    syllabus: [
      {
        week: "1",
        title: "Introduction to RPA",
        topics: [
          "What is RPA and why it matters",
          "Benefits & use cases of automation",
          "RPA lifecycle overview",
          "Installing UiPath Studio",
          "Building your first robot",
          "RPA Process Candidate Selection"
        ],
        tools: ["UiPath Studio", "UiPath Assistant"]
      },
      {
        week: "2",
        title: "Exploring UiPath Studio",
        topics: [
          "UiPath Interface Overview",
          "Design, Execute, and Debug Ribbons",
          "Activities & Snippets Panel",
          "Variables and Data Types",
          "Arguments and Data Transfer",
          "Workflow and Sequence Basics"
        ],
        tools: ["UiPath Studio", "Google Chrome Extension"]
      },
      {
        week: "3",
        title: "Core Automation Concepts",
        topics: [
          "Recording and Playback",
          "Selectors and UiExplorer",
          "Data Scraping and Web Automation",
          "Flowcharts and Control Flow",
          "String Manipulation & Conditions"
        ],
        tools: ["UiExplorer", "Data Scraping Wizard"]
      },
      {
        week: "4",
        title: "Excel & Data Handling",
        topics: [
          "Reading and Writing Excel Files",
          "Excel Application Scope",
          "DataTables and Loops",
          "Error Handling and Try-Catch",
          "Invoke Workflow Files"
        ],
        tools: ["UiPath.Excel.Activities", "REFramework"]
      },
      {
        week: "5",
        title: "PDF & OCR Automation",
        topics: [
          "Working with PDFs",
          "Data Extraction from PDF",
          "Using OCR for Image-based PDFs",
          "Selectors and Wildcards",
          "Exporting to Excel"
        ],
        tools: ["Tesseract OCR", "UiPath PDF Activities"]
      },
      {
        week: "6",
        title: "Email & Web Automation",
        topics: [
          "Email Automation with Outlook and Gmail",
          "Reading, Sending, and Filtering Emails",
          "Attachments and SMTP Configuration",
          "Web Automation with Input Dialogs",
          "Data Entry Automation"
        ],
        tools: ["UiPath.Mail.Activities", "SMTP", "IMAP"]
      },
      {
        week: "7",
        title: "Advanced Automation & Integration",
        topics: [
          "Using APIs within RPA",
          "Working with Orchestrator",
          "Version Control in UiPath",
          "Credential Management",
          "Parallel Execution and Triggers"
        ],
        tools: ["UiPath Orchestrator", "Postman", "Git"]
      },
      {
        week: "8",
        title: "Deployment & Certification",
        topics: [
          "Publishing Bots to Orchestrator",
          "Scheduling and Monitoring Jobs",
          "Real-World Automation Use Cases",
          "Automation Anywhere Overview",
          "Certification Preparation"
        ],
        tools: ["UiPath Academy", "Automation Anywhere A360"]
      }
    ]
  },
  {
    id: "16",
    title: "Java Full Stack Development",
    instructor: "Michael Thomas",
    instructorTitle: "Senior Full-Stack Architect",
    instructorImage: "/instructor-michael-thomas.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 7200,
    growthRate: 180,
    color: "from-indigo-600 to-purple-700",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "End-to-end mastery of Java, Spring Boot, React, MySQL, and DevOps tools for building and deploying scalable enterprise-grade full stack applications.",
    syllabus: [
      {
        week: "1-4",
        title: "Core Java & OOP Fundamentals",
        topics: [
          "Java Introduction – Features, JDK, JVM, and JRE",
          "Setting up Development Environment (IntelliJ / Eclipse)",
          "Data Types, Variables, Operators, and Control Flow",
          "Arrays and Strings",
          "Object-Oriented Programming – Class, Object, Methods, Constructors",
          "Encapsulation, Inheritance, Polymorphism, Abstraction",
          "Method Overloading & Overriding",
          "Interfaces and Abstract Classes",
          "Exception Handling – try, catch, finally, throw, throws",
          "Java I/O Streams, File Handling, and Serialization",
          "Collections Framework – List, Set, Map, Iterator, Comparator",
          "Generics, Wrapper Classes, Enum, Autoboxing & Unboxing",
          "Multithreading – Thread Lifecycle, Synchronization, Deadlock, Inter-thread Communication",
          "Lambda Expressions, Stream API, and Functional Interfaces",
          "Java 8+ Features Overview"
        ],
        tools: ["IntelliJ IDEA", "JDK 17", "Maven"]
      },
      {
        week: "5-8",
        title: "Advanced Java (JDBC, Servlets, JSP)",
        topics: [
          "Introduction to Java EE (Jakarta EE)",
          "JDBC Architecture and API",
          "Database Connectivity using JDBC (CRUD operations)",
          "Statement, PreparedStatement, CallableStatement",
          "ResultSet and Metadata Handling",
          "Servlets Lifecycle and Configuration",
          "Handling Form Data and Sessions",
          "Cookies and URL Rewriting",
          "JSP Basics – Directives, Scriptlets, EL, and JSTL",
          "MVC Design Pattern in Web Apps",
          "Integrating JSP + Servlets + JDBC",
          "Error Handling & Deployment on Tomcat Server"
        ],
        tools: ["Apache Tomcat", "MySQL Workbench", "Postman"]
      },
      {
        week: "9-12",
        title: "Spring Framework & Spring Boot",
        topics: [
          "Spring Core Concepts – IoC, DI, and Beans",
          "Spring Boot Setup and Project Structure",
          "Spring Boot Starters and Auto Configuration",
          "Spring MVC Architecture – Controllers, Views, and Models",
          "Creating RESTful APIs with Spring Boot",
          "Request Mapping, Validation, Exception Handling",
          "Spring Boot Profiles, Configurations, and Actuator",
          "Spring Security – Authentication & Authorization (JWT, OAuth2)",
          "Integration with Frontend using REST APIs",
          "Testing Spring Applications (JUnit, Mockito)"
        ],
        tools: ["Spring Boot", "Spring Initializr", "Postman", "STS"]
      },
      {
        week: "13-16",
        title: "Hibernate & JPA + Microservices",
        topics: [
          "ORM Basics and Advantages",
          "Hibernate Architecture and Configuration",
          "Entity Mapping (One-to-One, One-to-Many, Many-to-Many)",
          "JPQL and Criteria API",
          "Lazy & Eager Loading",
          "Caching (First & Second Level)",
          "Spring Data JPA – Repository Layer Integration",
          "Microservices Architecture Fundamentals",
          "Spring Cloud Components – Eureka, API Gateway, Config Server",
          "Inter-Service Communication using Feign Client and RestTemplate",
          "Centralized Logging and Distributed Tracing",
          "Dockerizing Microservices"
        ],
        tools: ["Hibernate", "Spring Data JPA", "Docker", "Postman"]
      },
      {
        week: "17-20",
        title: "Frontend Development (HTML, CSS, JavaScript, React)",
        topics: [
          "HTML5 – Structure, Forms, Semantic Tags",
          "CSS3 – Flexbox, Grid, Responsive Design, Animations",
          "Bootstrap Framework Basics",
          "JavaScript Fundamentals – ES6+, Functions, Objects, DOM",
          "Asynchronous JS – Callbacks, Promises, Async/Await",
          "React Introduction & Setup",
          "JSX, Components, Props, State, Lifecycle",
          "Hooks (useState, useEffect, useRef)",
          "React Router – Navigation & Routing",
          "API Integration using Axios/Fetch",
          "Building Responsive Frontend with React",
          "Connecting React Frontend to Spring Boot Backend"
        ],
        tools: ["VS Code", "Node.js", "React", "Bootstrap"]
      },
      {
        week: "21-22",
        title: "Database, Git & DevOps",
        topics: [
          "Relational Databases (MySQL) – DDL, DML, Joins, Views",
          "NoSQL Introduction (MongoDB) – Collections & CRUD",
          "Database Connection Pooling",
          "Version Control using Git & GitHub",
          "Branching, Merging, Pull Requests, and CI Basics",
          "Maven & Build Lifecycle",
          "Docker – Containers, Images, Volumes, Dockerfile",
          "Deploying Spring Boot App inside Docker Container"
        ],
        tools: ["MySQL", "MongoDB", "Git", "Docker"]
      },
      {
        week: "23-24",
        title: "Final Project & Cloud Deployment",
        topics: [
          "Building a Full Stack Project (Frontend + Backend + DB)",
          "Creating REST APIs and Connecting to React UI",
          "Authentication & Authorization (JWT)",
          "Deploying Backend on AWS EC2 / Elastic Beanstalk",
          "Hosting Frontend on Vercel / Netlify",
          "Connecting Cloud Database (RDS / Mongo Atlas)",
          "CI/CD Pipeline Setup (GitHub Actions)",
          "Monitoring Logs & Performance",
          "Interview Preparation and Mock Tests"
        ],
        tools: ["AWS", "Vercel", "GitHub Actions", "MongoDB Atlas"]
      }
    ]
  },
  {
    id: "17",
    title: "DevOps Mastery",
    instructor: "Sophie Miller",
    instructorTitle: "Senior DevOps Engineer",
    instructorImage: "/instructor-sophie-miller.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 6800,
    growthRate: 165,
    color: "from-sky-500 to-blue-600",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "End-to-end DevOps training — CI/CD, Jenkins, Docker, Kubernetes, Terraform, and Cloud Automation for modern infrastructure.",
    syllabus: [
      {
        week: "1-4",
        title: "Linux Fundamentals & Git Version Control",
        topics: [
          "Introduction to DevOps and its Principles",
          "Linux Command Line, File System, Permissions",
          "Package Management, Shell Scripting, and Cron Jobs",
          "System Monitoring and Log Management",
          "Networking Basics for DevOps (Ports, SSH, Firewalls)",
          "Version Control with Git – Init, Clone, Commit, Branching, Merging",
          "GitHub and GitLab Workflows",
          "Git Hooks, Rebasing, Pull Requests, and Conflict Resolution"
        ],
        tools: ["Ubuntu", "Git", "GitHub", "Shell Script"]
      },
      {
        week: "5-8",
        title: "Continuous Integration (CI) with Jenkins & Maven",
        topics: [
          "Introduction to CI/CD Concepts",
          "Setting up Jenkins on Linux",
          "Creating and Managing Jenkins Jobs",
          "Pipeline as Code using Jenkinsfile",
          "Integrating Git, Maven, and Webhooks with Jenkins",
          "Automating Build, Test, and Deploy Stages",
          "Configuring Jenkins Agents and Nodes",
          "Monitoring Build Logs and Notifications"
        ],
        tools: ["Jenkins", "Maven", "Nexus", "SonarQube"]
      },
      {
        week: "9-12",
        title: "Containerization with Docker & Orchestration with Kubernetes",
        topics: [
          "Docker Installation and Architecture",
          "Creating Dockerfiles and Managing Images",
          "Working with Containers and Volumes",
          "Docker Compose – Multi-container Setup",
          "Docker Networking and Private Registries",
          "Introduction to Kubernetes (K8s)",
          "K8s Architecture – Pods, Deployments, Services, Ingress",
          "Managing Clusters using kubectl",
          "Scaling, Rolling Updates, and Helm Charts"
        ],
        tools: ["Docker", "Docker Compose", "Kubernetes", "Minikube", "Helm"]
      },
      {
        week: "13-16",
        title: "Infrastructure as Code (IaC) with Terraform & Ansible",
        topics: [
          "Infrastructure as Code Concepts",
          "Terraform Setup, Providers, Variables, and States",
          "Building AWS Resources with Terraform",
          "Terraform Modules and Workspaces",
          "Configuration Management using Ansible",
          "Playbooks, Roles, and Inventory Files",
          "Integrating Terraform and Ansible",
          "Environment Provisioning and Automation"
        ],
        tools: ["Terraform", "Ansible", "AWS Free Tier"]
      },
      {
        week: "17-20",
        title: "Monitoring, Logging & Cloud DevOps",
        topics: [
          "Monitoring with Prometheus and Grafana",
          "Centralized Logging with ELK (Elasticsearch, Logstash, Kibana)",
          "Container Monitoring with cAdvisor and Node Exporter",
          "Cloud Infrastructure Monitoring",
          "Introduction to AWS and Azure for DevOps Engineers",
          "Cloud CI/CD and Deployment Automation",
          "Backup and Disaster Recovery Strategies"
        ],
        tools: ["Prometheus", "Grafana", "ELK Stack", "AWS CloudWatch"]
      },
      {
        week: "21-24",
        title: "Capstone Project & CI/CD Automation",
        topics: [
          "End-to-End Pipeline Setup (Git → Jenkins → Docker → Kubernetes → Monitoring)",
          "Implement Blue/Green Deployment Strategy",
          "Integrate Security in DevOps (DevSecOps)",
          "Deploy a Java Spring Boot App on AWS using Jenkins Pipeline",
          "Terraform Infrastructure Deployment Automation",
          "Logging and Alerting Dashboards",
          "Final Capstone Project and Interview Preparation"
        ],
        tools: ["Jenkins", "Docker", "Kubernetes", "Terraform", "AWS", "Prometheus"]
      }
    ]
  },

  {
    id: "18",
    title: "AWS Cloud Engineer",
    instructor: "Rajesh Patel",
    instructorTitle: "AWS Certified Solutions Architect",
    instructorImage: "/instructor-rajesh-patel.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 8200,
    growthRate: 175,
    color: "from-yellow-400 to-orange-500",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Comprehensive AWS Cloud course covering EC2, S3, Lambda, RDS, CloudFormation, IAM, and DevOps implementation on AWS.",
    syllabus: [
      {
        week: "1-4",
        title: "AWS Foundation & IAM",
        topics: [
          "Introduction to Cloud Computing and AWS Global Infrastructure",
          "AWS Free Tier Account Setup",
          "AWS CLI and SDK Setup",
          "Identity and Access Management (IAM)",
          "Users, Groups, Roles, Policies, and MFA",
          "Billing, Cost Management, and Budgets"
        ],
        tools: ["AWS Management Console", "AWS CLI", "IAM"]
      },
      {
        week: "5-8",
        title: "Compute & Networking Services",
        topics: [
          "Amazon EC2 Setup and Management",
          "Elastic Load Balancing (ELB) and Auto Scaling",
          "Amazon Lightsail Overview",
          "Elastic IPs, Security Groups, Key Pairs",
          "Amazon VPC – Subnets, Route Tables, NAT Gateways",
          "VPN Connections and Peering"
        ],
        tools: ["EC2", "VPC", "Elastic Load Balancer"]
      },
      {
        week: "9-12",
        title: "Storage, Databases & Serverless Computing",
        topics: [
          "Amazon S3 – Buckets, Lifecycle Policies, Versioning, Encryption",
          "Amazon Glacier and Storage Gateway",
          "RDS – MySQL, PostgreSQL, and Aurora",
          "DynamoDB – NoSQL Basics",
          "Lambda Functions and Event Triggers",
          "API Gateway Integration",
          "CloudFront and Route53"
        ],
        tools: ["S3", "RDS", "DynamoDB", "Lambda", "API Gateway"]
      },
      {
        week: "13-16",
        title: "DevOps on AWS & Infrastructure Automation",
        topics: [
          "AWS CodePipeline, CodeBuild, and CodeDeploy",
          "AWS Elastic Beanstalk",
          "Infrastructure as Code using CloudFormation",
          "AWS CDK (Cloud Development Kit)",
          "Container Management – ECS and EKS",
          "Monitoring with CloudWatch and CloudTrail"
        ],
        tools: ["CloudFormation", "CodePipeline", "EKS", "ECS", "CloudWatch"]
      },
      {
        week: "17-20",
        title: "Security, Identity & Governance",
        topics: [
          "KMS and Encryption Management",
          "AWS Config and Inspector",
          "Security Hub and GuardDuty",
          "WAF (Web Application Firewall) and Shield",
          "Backup and Recovery",
          "Best Practices in Security and Compliance"
        ],
        tools: ["AWS WAF", "KMS", "GuardDuty", "Inspector"]
      },
      {
        week: "21-24",
        title: "Final Project & Certification Preparation",
        topics: [
          "Deploying a Full Stack App using EC2 + RDS + S3 + CloudFront",
          "Implementing CI/CD with CodePipeline and Lambda",
          "Cost Optimization and Monitoring",
          "AWS Certified Solutions Architect Associate Exam Prep",
          "Mock Tests and Hands-On Labs"
        ],
        tools: ["AWS EC2", "RDS", "S3", "CloudFront", "CodePipeline"]
      }
    ]
  },

  {
    id: "19",
    title: "Microsoft Azure Expert",
    instructor: "Emma Robinson",
    instructorTitle: "Azure Solutions Architect",
    instructorImage: "/instructor-emma-robinson.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 6400,
    growthRate: 160,
    color: "from-blue-700 to-cyan-600",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Master Azure Cloud Services including Compute, Storage, Networking, Azure DevOps, Security, and ARM Templates with real-world hands-on labs.",
    syllabus: [
      {
        week: "1-4",
        title: "Azure Fundamentals",
        topics: [
          "Understanding Cloud Concepts and Azure Architecture",
          "Azure Portal, CLI, and PowerShell",
          "Azure Resource Manager (ARM)",
          "Subscription, Resource Groups, and Tags",
          "Identity and Access Management using Azure AD",
          "RBAC and Azure Policy"
        ],
        tools: ["Azure Portal", "Azure CLI", "PowerShell"]
      },
      {
        week: "5-8",
        title: "Compute & Networking",
        topics: [
          "Virtual Machines – Creation, Configuration, and Scaling",
          "VM Availability Sets, Zones, and Load Balancer",
          "Azure Virtual Network (VNet), Subnets, NSG",
          "Azure DNS, ExpressRoute, and VPN Gateway",
          "Application Gateway and Front Door",
          "Azure Bastion and Private Endpoints"
        ],
        tools: ["Azure VMs", "VNet", "App Gateway", "Load Balancer"]
      },
      {
        week: "9-12",
        title: "Storage & Databases",
        topics: [
          "Azure Storage Accounts and Containers",
          "Blob, File, Queue, and Table Storage",
          "Azure SQL Database and Managed Instances",
          "Cosmos DB (NoSQL)",
          "Data Factory and Synapse Analytics Basics",
          "Backup and Restore Policies"
        ],
        tools: ["Azure Storage", "SQL Database", "Cosmos DB", "Data Factory"]
      },
      {
        week: "13-16",
        title: "Azure DevOps & CI/CD Automation",
        topics: [
          "Azure DevOps Organization and Repos",
          "Pipelines – YAML and Classic",
          "Build, Test, and Deploy Automation",
          "Artifacts and Release Management",
          "Integrating GitHub and Azure Boards",
          "Containerization with Docker on Azure",
          "Kubernetes with AKS (Azure Kubernetes Service)"
        ],
        tools: ["Azure DevOps", "GitHub", "AKS", "Docker"]
      },
      {
        week: "17-20",
        title: "Security, Monitoring & Governance",
        topics: [
          "Azure Monitor and Log Analytics",
          "Application Insights for App Monitoring",
          "Security Center and Defender for Cloud",
          "Azure Key Vault and Managed Identity",
          "Compliance Management and Cost Optimization"
        ],
        tools: ["Azure Monitor", "Security Center", "Key Vault"]
      },
      {
        week: "21-24",
        title: "Final Project & Certification Prep",
        topics: [
          "Building an End-to-End CI/CD App Pipeline on Azure",
          "Hosting a Web App with Azure App Service + SQL DB + Key Vault",
          "Implementing ARM Templates and Bicep Scripts",
          "Deploying with AKS and Azure DevOps",
          "Preparing for AZ-104 & AZ-204 Certification",
          "Mock Tests and Interview Readiness"
        ],
        tools: ["Azure App Service", "Azure SQL", "Azure DevOps", "Bicep"]
      }
    ]
  },
  {
    id: "20",
    title: "Aptitude Mastery Program",
    instructor: "Ananya Sharma",
    instructorTitle: "Quantitative Aptitude & Reasoning Expert",
    instructorImage: "/instructor-ananya-sharma.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 7800,
    growthRate: 210,
    color: "from-blue-600 to-indigo-500",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Master Quantitative Aptitude, Logical Reasoning, and Verbal Ability to crack placement exams, competitive tests, and interviews with confidence.",
    syllabus: [
      {
        week: "1-4",
        title: "Quantitative Foundations",
        topics: [
          "Number System and Divisibility Rules",
          "LCM, HCF, and Factors",
          "Simplification and Approximation",
          "Percentage, Ratio & Proportion",
          "Average and Alligation",
          "Problems on Ages and Partnership"
        ],
        tools: ["Practice Sheets", "Mock Tests", "Live Quizzes"]
      },
      {
        week: "5-8",
        title: "Arithmetic & Data Handling",
        topics: [
          "Profit, Loss, and Discount",
          "Simple and Compound Interest",
          "Time, Speed, and Distance",
          "Work and Wages",
          "Boats, Trains, and Pipes",
          "Data Interpretation – Tables, Graphs, and Charts"
        ],
        tools: ["Excel Practice", "Aptitude Tracker", "Timed Assessments"]
      },
      {
        week: "9-12",
        title: "Logical Reasoning – Core Concepts",
        topics: [
          "Coding-Decoding and Blood Relations",
          "Direction Sense and Ranking",
          "Series and Analogy",
          "Puzzles and Seating Arrangements",
          "Syllogisms and Logical Deductions",
          "Input-Output and Machine Test Logic"
        ],
        tools: ["Reasoning Workbook", "Puzzle Practice", "Visual Logic Trainer"]
      },
      {
        week: "13-16",
        title: "Advanced Quant & Analytical Skills",
        topics: [
          "Permutations and Combinations",
          "Probability and Set Theory",
          "Mensuration and Geometry Basics",
          "Trigonometry Applications in Aptitude",
          "Data Sufficiency and Case Studies",
          "Mixed Word Problems"
        ],
        tools: ["MathLab Sheets", "Concept Videos", "Mini Assessments"]
      },
      {
        week: "17-20",
        title: "Verbal Ability & Communication",
        topics: [
          "Grammar and Sentence Correction",
          "Reading Comprehension Strategies",
          "Synonyms, Antonyms, and Vocabulary Building",
          "Para Jumbles and Cloze Tests",
          "Critical Reasoning",
          "Email and Report Writing for Interviews"
        ],
        tools: ["Vocabulary Builder App", "Grammar Cards", "Reading Tracker"]
      },
      {
        week: "21-24",
        title: "Mock Tests & Placement Prep",
        topics: [
          "Company-wise Aptitude Test Patterns (TCS, Infosys, Wipro, etc.)",
          "Full-Length Sectional Tests – Quant, Reasoning, Verbal",
          "Group Discussion & Interview Preparation",
          "Performance Analytics & Improvement Plan",
          "Final Capstone Aptitude Challenge",
          "Certification & Job Readiness Evaluation"
        ],
        tools: ["Mock Test Portal", "Performance Dashboard", "Certification Exam"]
      }
    ]
  },
  {
    id: "21",
    title: "Soft Skills & Personality Development",
    instructor: "Rahul Mehta",
    instructorTitle: "Corporate Trainer & Communication Coach",
    instructorImage: "/instructor-rahul-mehta.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 6200,
    growthRate: 190,
    color: "from-indigo-600 to-blue-500",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Enhance your communication, confidence, and emotional intelligence to excel in interviews, presentations, and professional collaborations.",
    syllabus: [
      {
        week: "1-4",
        title: "Communication Essentials",
        topics: [
          "Importance of Communication in Professional Success",
          "Verbal and Non-Verbal Communication",
          "Body Language and Eye Contact",
          "Active Listening and Empathy",
          "Tone, Clarity, and Confidence in Speaking",
          "Common Grammar and Pronunciation Mistakes"
        ],
        tools: ["Live Speaking Sessions", "Voice Recording Practice", "Feedback Sheets"]
      },
      {
        week: "5-8",
        title: "Interpersonal & Team Skills",
        topics: [
          "Building Trust and Rapport in Teams",
          "Conflict Management and Collaboration",
          "Leadership vs. Followership",
          "Understanding Workplace Dynamics",
          "Cultural Awareness and Diversity",
          "Team Building Activities and Role Plays"
        ],
        tools: ["Group Activities", "Role Play Decks", "Peer Feedback Tools"]
      },
      {
        week: "9-12",
        title: "Presentation Mastery",
        topics: [
          "Planning and Structuring a Presentation",
          "Designing Engaging Slides",
          "Storytelling Techniques for Impact",
          "Handling Stage Fear and Nervousness",
          "Voice Modulation and Audience Engagement",
          "Delivering Presentations with Confidence"
        ],
        tools: ["PowerPoint", "Canva", "Public Speaking Practice"]
      },
      {
        week: "13-16",
        title: "Professional Etiquette & Workplace Behavior",
        topics: [
          "Email and Business Writing Skills",
          "Meeting and Telephone Etiquette",
          "Time Management and Productivity",
          "Dress Code and Grooming Standards",
          "Ethics and Professional Conduct",
          "Workplace Responsibility and Ownership"
        ],
        tools: ["Business Writing Toolkit", "Etiquette Scenarios", "Time Planner"]
      },
      {
        week: "17-20",
        title: "Emotional Intelligence & Personal Growth",
        topics: [
          "Understanding and Managing Emotions",
          "Empathy and Relationship Management",
          "Self-Awareness and Self-Regulation",
          "Handling Criticism and Stress",
          "Developing Positive Thinking and Growth Mindset",
          "Mindfulness and Work-Life Balance"
        ],
        tools: ["Self-Reflection Journal", "Mindfulness Exercises", "Group Discussions"]
      },
      {
        week: "21-24",
        title: "Interview Readiness & Career Branding",
        topics: [
          "Resume and Cover Letter Writing",
          "Personal Branding and LinkedIn Optimization",
          "Common HR and Technical Interview Questions",
          "Mock Interviews and Feedback",
          "Group Discussions and Case Studies",
          "Final Presentation and Certification"
        ],
        tools: ["Resume Builder", "Mock Interview Panel", "LinkedIn Workshop"]
      }
    ]
  },

  {
    id: "22",
    title: "Google Cloud Platform (GCP)",
    instructor: "Priya Nair",
    instructorTitle: "Cloud Architect & Google Certified Trainer",
    instructorImage: "/instructor-priya-nair.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 5400,
    growthRate: 210,
    color: "from-sky-500 to-cyan-400",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Master Google Cloud Platform (GCP) fundamentals and advanced services — from compute and networking to data analytics and AI integration — to build scalable, secure, and automated cloud solutions.",
    syllabus: [
      {
        week: "1-4",
        title: "GCP Fundamentals & Core Services",
        topics: [
          "Introduction to Cloud Computing and GCP Ecosystem",
          "Setting Up GCP Console, SDK, and IAM Roles",
          "Understanding Compute Engine and Virtual Machines",
          "Networking Basics: VPC, Subnets, and Firewalls",
          "Cloud Storage and Cloud SQL Fundamentals",
          "Monitoring and Logging with Cloud Operations Suite"
        ],
        tools: [
          "Google Cloud Console",
          "Cloud SDK (gcloud CLI)",
          "Compute Engine",
          "Cloud Storage"
        ]
      },
      {
        week: "5-8",
        title: "Containers, Kubernetes, and Serverless Computing",
        topics: [
          "Introduction to Docker and Containerization",
          "Google Kubernetes Engine (GKE) Setup and Management",
          "Deploying and Scaling Applications on GKE",
          "Serverless Architecture using Cloud Run and Cloud Functions",
          "Integrating Pub/Sub for Event-Driven Workflows",
          "Monitoring and Logging Containerized Apps"
        ],
        tools: [
          "Docker",
          "Kubernetes (GKE)",
          "Cloud Run",
          "Cloud Functions",
          "Pub/Sub"
        ]
      },
      {
        week: "9-12",
        title: "Networking, Security, and IAM",
        topics: [
          "VPC Peering, Cloud DNS, and Load Balancing",
          "Hybrid Connectivity (VPN, Interconnect)",
          "Identity and Access Management (IAM) Deep Dive",
          "Security Policies, Firewalls, and Cloud Armor",
          "Cloud KMS and Secret Manager",
          "Best Practices for Secure GCP Deployments"
        ],
        tools: [
          "VPC Networks",
          "Cloud Armor",
          "Cloud KMS",
          "Secret Manager",
          "IAM Policies"
        ]
      },
      {
        week: "13-16",
        title: "Data Engineering & Big Data Analytics on GCP",
        topics: [
          "Data Ingestion with Cloud Storage and Pub/Sub",
          "ETL with Dataflow and Dataprep",
          "BigQuery: Querying and Optimizing Large Datasets",
          "Data Visualization with Looker Studio",
          "Machine Learning with BigQuery ML",
          "Cost Optimization and Resource Monitoring"
        ],
        tools: [
          "BigQuery",
          "Dataflow",
          "Dataproc",
          "Looker Studio",
          "BigQuery ML"
        ]
      },
      {
        week: "17-20",
        title: "DevOps, Automation, and CI/CD on GCP",
        topics: [
          "Infrastructure as Code with Terraform",
          "Using Cloud Deployment Manager",
          "CI/CD Pipelines with Cloud Build and Artifact Registry",
          "Automating Deployments to GKE and Cloud Run",
          "Observability with Cloud Monitoring & Logging",
          "Backup and Disaster Recovery Strategies"
        ],
        tools: [
          "Terraform",
          "Cloud Build",
          "Artifact Registry",
          "Cloud Monitoring",
          "Deployment Manager"
        ]
      },
      {
        week: "21-24",
        title: "Capstone Project & Certification Preparation",
        topics: [
          "Designing Scalable and Secure Cloud Architecture",
          "End-to-End Cloud Deployment Project",
          "Performance and Cost Optimization Strategies",
          "Preparing for Google Cloud Architect Certification",
          "Mock Exams and Hands-On Lab Challenges",
          "Final Presentation, Feedback & Certification"
        ],
        tools: [
          "Qwiklabs",
          "Google Cloud Skill Boost",
          "Architecture Diagrams",
          "Certification Practice Tests"
        ]
      }
    ]
  },
  {
    id: "23",
    title: "ML & AI",
    instructor: "Dr. Ananya Verma",
    instructorTitle: "AI Researcher & Data Science Mentor",
    instructorImage: "/instructor-ananya-verma.jpg",
    isProgramming: true,
    weeks: 24,
    baseStudents: 7800,
    growthRate: 230,
    color: "from-emerald-500 to-teal-400",
    icon: <Code2 className="w-6 h-6" />,
    description:
      "Become an expert in machine learning and artificial intelligence through hands-on projects, model deployment, and real-world problem-solving using Python, TensorFlow, and Scikit-learn.",
    syllabus: [
      {
        week: "1-4",
        title: "Python for Data Science & ML Foundations",
        topics: [
          "Introduction to AI, ML, and Data Science Ecosystem",
          "Python Refresher: NumPy, Pandas, Matplotlib, Seaborn",
          "Data Cleaning, Transformation, and Visualization",
          "Exploratory Data Analysis (EDA)",
          "Feature Engineering Basics",
          "Understanding Bias, Variance, and Evaluation Metrics"
        ],
        tools: [
          "Python",
          "Jupyter Notebook",
          "Pandas",
          "Matplotlib",
          "Seaborn"
        ]
      },
      {
        week: "5-8",
        title: "Supervised Learning Algorithms",
        topics: [
          "Linear and Logistic Regression from Scratch",
          "Decision Trees and Random Forests",
          "Support Vector Machines (SVM)",
          "k-Nearest Neighbors (k-NN)",
          "Naive Bayes Classifier",
          "Model Evaluation and Cross-Validation"
        ],
        tools: [
          "Scikit-learn",
          "NumPy",
          "Matplotlib",
          "Joblib"
        ]
      },
      {
        week: "9-12",
        title: "Unsupervised Learning & Feature Techniques",
        topics: [
          "Clustering with K-Means, DBSCAN, and Hierarchical Models",
          "Dimensionality Reduction using PCA and t-SNE",
          "Association Rule Mining (Apriori, FP-Growth)",
          "Anomaly Detection Techniques",
          "Feature Scaling and Selection Methods",
          "Applications in Customer Segmentation"
        ],
        tools: [
          "Scikit-learn",
          "Yellowbrick",
          "Pandas",
          "Plotly"
        ]
      },
      {
        week: "13-16",
        title: "Deep Learning & Neural Networks",
        topics: [
          "Introduction to Artificial Neural Networks (ANN)",
          "Activation Functions, Optimizers, and Backpropagation",
          "Building Models using TensorFlow & Keras",
          "Convolutional Neural Networks (CNNs)",
          "Recurrent Neural Networks (RNNs) & LSTMs",
          "Transfer Learning and Fine-Tuning Pretrained Models"
        ],
        tools: [
          "TensorFlow",
          "Keras",
          "NumPy",
          "Google Colab",
          "OpenCV"
        ]
      },
      {
        week: "17-20",
        title: "Model Deployment, MLOps & Advanced Topics",
        topics: [
          "Model Saving and Serialization (Pickle, Joblib)",
          "Building APIs with Flask/FastAPI",
          "Deployment on AWS, GCP, or Streamlit",
          "Automated ML Pipelines with MLflow",
          "Hyperparameter Tuning and Model Optimization",
          "Explainable AI (SHAP, LIME)"
        ],
        tools: [
          "Flask",
          "Streamlit",
          "MLflow",
          "AWS/GCP",
          "Docker"
        ]
      },
      {
        week: "21-24",
        title: "Capstone Project & AI Ethics",
        topics: [
          "End-to-End ML Project: From Data Collection to Deployment",
          "Building a Predictive or Classification Model",
          "Documentation and Model Presentation",
          "AI Ethics, Fairness, and Responsible AI Practices",
          "Version Control with Git & GitHub",
          "Final Evaluation, Presentation, and Certification"
        ],
        tools: [
          "Git & GitHub",
          "Jupyter Notebook",
          "Streamlit / Flask",
          "Presentation Decks"
        ]
      }
    ]
  },

  {
    id: "24",
    title: "Business Intelligence (BI) Tools",
    instructor: "Rohit Malhotra",
    instructorTitle: "Senior BI Consultant & Data Visualization Expert",
    instructorImage: "/instructor-rohit-malhotra.jpg",
    isProgramming: false,
    weeks: 24,
    baseStudents: 6900,
    growthRate: 200,
    color: "from-amber-500 to-orange-400",
    icon: <BarChart3 className="w-6 h-6" />,
    description:
      "Master Tableau and Power BI to transform raw data into stunning dashboards and actionable business insights. Learn real-world data modeling, visualization, storytelling, and DAX formula building for professional BI roles.",
    syllabus: [
      {
        week: "1-4",
        title: "Introduction to Business Intelligence & Data Visualization",
        topics: [
          "Understanding BI Concepts and Architecture",
          "Importance of Data Visualization in Decision Making",
          "Overview of Tableau & Power BI Interfaces",
          "Connecting to Data Sources (Excel, SQL, CSV, Cloud)",
          "Basic Data Cleaning and Transformation",
          "Creating Your First Dashboard"
        ],
        tools: [
          "Tableau Desktop",
          "Power BI Desktop",
          "Excel / CSV Data",
          "Sample Superstore Dataset"
        ]
      },
      {
        week: "5-8",
        title: "Data Modeling, Joins & Relationships",
        topics: [
          "Understanding Data Models and Schema Design",
          "Creating Relationships between Tables",
          "Joins, Blending, and Unions in Tableau and Power BI",
          "Data Transformation using Power Query Editor",
          "Data Normalization & Hierarchies",
          "Best Practices for Model Performance"
        ],
        tools: [
          "Tableau Data Model",
          "Power Query Editor",
          "SQL Data Connections"
        ]
      },
      {
        week: "9-12",
        title: "Advanced Calculations & DAX Formulas",
        topics: [
          "Calculated Fields and Table Calculations in Tableau",
          "Introduction to DAX (Data Analysis Expressions)",
          "Creating Measures and Calculated Columns",
          "Aggregations, Filters, and Context in DAX",
          "Time Intelligence Functions (YTD, QTD, MTD)",
          "KPI and Dynamic Measure Creation"
        ],
        tools: [
          "DAX Studio",
          "Power BI Desktop",
          "Tableau Calculated Fields"
        ]
      },
      {
        week: "13-16",
        title: "Interactive Dashboards & Visual Storytelling",
        topics: [
          "Design Principles for Effective Dashboards",
          "Advanced Visuals: Maps, Scatterplots, and Heatmaps",
          "Dynamic Parameters and Filters",
          "Actions and Drill-Down Reports",
          "Storytelling with Data in Tableau",
          "Custom Visuals and AppSource Integrations in Power BI"
        ],
        tools: [
          "Tableau Story",
          "Power BI Service",
          "AppSource Custom Visuals",
          "Mapbox"
        ]
      },
      {
        week: "17-20",
        title: "Data Integration, Automation & Publishing",
        topics: [
          "Publishing Dashboards to Tableau Server / Power BI Service",
          "Setting Up Data Gateways for Scheduled Refresh",
          "Row-Level Security (RLS) and Data Governance",
          "Integration with Excel, SQL Server, and Google Sheets",
          "Embedding BI Reports into Web Apps",
          "Collaboration and Version Control"
        ],
        tools: [
          "Tableau Server / Online",
          "Power BI Service",
          "Power Automate",
          "SQL Server"
        ]
      },
      {
        week: "21-24",
        title: "Capstone Projects & Certification Readiness",
        topics: [
          "Real-Time BI Project using Multiple Data Sources",
          "Tableau Dashboard: Sales Performance & Profitability Analysis",
          "Power BI Dashboard: Financial and KPI Reporting",
          "Performance Optimization and UX Enhancements",
          "Preparing for Tableau Desktop Specialist & PL-300 (Power BI) Exams",
          "Final Presentation and Portfolio Showcase"
        ],
        tools: [
          "Sample Company Dataset",
          "Tableau Public",
          "Power BI Service",
          "Certification Practice Labs"
        ]
      }
    ]
  }





];

type CategoryKey =
  | "all"
  | "software-development"
  | "software-testing"
  | "data-science-analytics"
  | "data-engineer"
  | "devops"
  | "banking"
  | "ai-ml"
  | "cloud-computing"
  | "agile-scrum"
  | "software-architecture"
  | "aptitude-soft-skills";

const categoryMap: Record<CategoryKey, string[]> = {
  all: syallabus.map((c) => c.id),

  "software-development": ["1", "2", "3", "4", "13", "14", "16"],
  "software-testing": ["9"],
  "data-science-analytics": ["5", "6", "24"],
  "data-engineer": [],
  devops: ["17"],
  banking: [],
  "ai-ml": ["5", "23"],
  "cloud-computing": ["18", "19", "22"],
  "agile-scrum": [],
  "software-architecture": ["14"],
  "aptitude-soft-skills": ["20", "21"],
};

// ————————————————————————————————
// MAIN COMPONENT
// ————————————————————————————————

const categories = [
  { key: "software-development", label: "Software Development", icon: "💻" },
  { key: "software-testing", label: "Software Testing", icon: "🧪" },
  { key: "data-science-analytics", label: "Data Science & Analytics", icon: "📊" },
  { key: "data-engineer", label: "Data Engineer", icon: "🗄️" },
  { key: "devops", label: "DevOps", icon: "☁️" },
  { key: "banking", label: "Banking", icon: "🏦" },
  { key: "ai-ml", label: "AI / ML", icon: "🤖" },
  { key: "cloud-computing", label: "Cloud Computing", icon: "🌩️" },
  { key: "agile-scrum", label: "Agile & Scrum", icon: "📋" },
  { key: "software-architecture", label: "Software Architecture", icon: "🧱" },
  { key: "aptitude-soft-skills", label: "Aptitude & Soft Skills", icon: "💡" },
];

export default function CoursesPage() {
  const router = useRouter();


  const [counts, setCounts] = useState<Record<string, number>>({});
  const [dayCaps, setDayCaps] = useState<Record<string, number>>({});
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const monthStart = new Date(2025, 10, 1).getTime(); // Nov 1, 2025
  const now = Date.now();
  const daysSinceStart = Math.floor((now - monthStart) / (1000 * 60 * 60 * 24));

  // Initialize student counts
  useEffect(() => {
    const init: Record<string, number> = {};
    const caps: Record<string, number> = {};
    syallabus.forEach((c) => {
      const added = Math.min(daysSinceStart * c.growthRate, c.growthRate * 30);
      init[c.id] = c.baseStudents + added;
      caps[c.id] = c.growthRate * 30;
    });
    setCounts(init);
    setDayCaps(caps);
  }, []);

  // Live counter: +1 student every minute (random course)
  useEffect(() => {
    const interval = setInterval(() => {
      setCounts((prev) => {
        const next = { ...prev };
        const eligible = syallabus.filter(
          (c) => (next[c.id] ?? c.baseStudents) - c.baseStudents < dayCaps[c.id]
        );
        if (eligible.length === 0) return next;
        const chosen = eligible[Math.floor(Math.random() * eligible.length)];
        next[chosen.id] = (next[chosen.id] ?? chosen.baseStudents) + 1;
        return next;
      });
    }, 60_000);

    return () => clearInterval(interval);
  }, [dayCaps]);

  const handleEnroll = () => router.push("/contact");

  const filteredCourses =
    selectedCategory === "all"
      ? syallabus
      : syallabus.filter((c) => categoryMap[selectedCategory].includes(c.id));

  const [courseMode, setCourseMode] = useState<"online" | "offline">("online");


  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-gray-100">
      <Header />
    <section
        className="relative py-24 md:py-32 overflow-hidden min-h-screen flex items-center"
        style={{
          backgroundImage: 'url("/coursebanner.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Enhanced Overlay for Better Readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-indigo-900/60 to-blue-900/70" />
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Animated Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-6 md:px-12 max-w-7xl w-full">
          
          <div className="relative max-w-4xl mx-auto text-center space-y-8">
            
            {/* Badge */}
            <div className="inline-block animate-fade-in">
              <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-full border-2 border-blue-300 shadow-2xl">
                <span className="text-base md:text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  #1 Rated Training Platform
                </span>
              </div>
            </div>

            {/* Main Title with Enhanced Readability */}
            <h1 className="relative text-5xl md:text-7xl lg:text-8xl font-black leading-tight animate-fade-in-up">
              <span className="block text-white drop-shadow-2xl mb-4">
                Transform Your
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-indigo-200 to-purple-300 drop-shadow-2xl">
                Tech Career
              </span>
            </h1>

            {/* Description with Better Contrast */}
            <p className="relative text-xl md:text-2xl text-white leading-relaxed max-w-3xl mx-auto font-semibold drop-shadow-2xl animate-fade-in-up px-4" style={{ animationDelay: '0.2s' }}>
              Join the world's premier software training institute. We bridge the gap between 
              industry demands and academic learning, empowering the next generation of tech leaders.
            </p>

            {/* Stats Bar - Full Width on Mobile */}
            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="bg-white/15 backdrop-blur-xl px-8 py-6 rounded-2xl border-2 border-white/30 shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-4xl md:text-5xl font-black text-white drop-shadow-lg">5k+</div>
                <div className="text-base md:text-lg text-white/90 font-semibold mt-2">Active Students</div>
              </div>
              <div className="bg-white/15 backdrop-blur-xl px-8 py-6 rounded-2xl border-2 border-white/30 shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-4xl md:text-5xl font-black text-white drop-shadow-lg">3.5 LPA</div>
                <div className="text-base md:text-lg text-white/90 font-semibold mt-2">Avg. Salary</div>
              </div>
              <div className="bg-white/15 backdrop-blur-xl px-8 py-6 rounded-2xl border-2 border-white/30 shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-4xl md:text-5xl font-black text-white drop-shadow-lg">50+</div>
                <div className="text-base md:text-lg text-white/90 font-semibold mt-2">Partner Companies</div>
              </div>
            </div>

            {/* CTA Buttons - Full Width on Mobile */}
            <div className="relative pt-10 flex flex-col sm:flex-row justify-center gap-5 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Button
                onClick={handleEnroll}
                className="group bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-10 py-7 rounded-2xl font-bold text-lg md:text-xl shadow-2xl hover:shadow-blue-500/60 hover:scale-105 transition-all duration-300 border-2 border-white/30 w-full sm:w-auto"
              >
                <span className="flex items-center justify-center gap-3">
                  <span className="text-2xl">🚀</span>
                  Book Your Free Demo
                  <span className="group-hover:translate-x-2 transition-transform text-xl">→</span>
                </span>
              </Button>
              {/* <Button
                variant="outline"
                className="bg-white/20 backdrop-blur-xl text-white border-2 border-white/40 px-10 py-7 rounded-2xl font-bold text-lg md:text-xl hover:bg-white/30 transition-all duration-300 shadow-2xl w-full sm:w-auto"
              >
                View Courses
              </Button> */}
            </div>

            {/* Trust Indicators */}
            <div className="relative pt-12 flex flex-wrap justify-center gap-8 items-center opacity-90 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              <div className="text-white/80 text-sm md:text-base font-semibold flex items-center gap-2">
                <span className="text-green-400 text-xl">✓</span> Industry Certified
              </div>
              <div className="text-white/80 text-sm md:text-base font-semibold flex items-center gap-2">
                <span className="text-green-400 text-xl">✓</span> Secure Your Job
              </div>
              <div className="text-white/80 text-sm md:text-base font-semibold flex items-center gap-2">
                <span className="text-green-400 text-xl">✓</span> ISO 9001 Certified

              </div>
            </div>
          </div>

        </div>

      </section>
      <section>
        <div className="relative mt-0 bg-[#EAF2FF]/90 py-6 backdrop-blur-sm">
          <h2 className="text-center font-bold text-2xl text-gray-800 mb-4">
            Our Hiring Partners
          </h2>

          <Marquee gradient={false} speed={60} pauseOnHover>
            <div className="flex items-center gap-12 text-xl font-semibold text-blue-600">
              <span>Kotak Mahindra Bank</span>
              <span>nference</span>
              <span>Saint-Gobain</span>
              <span>XpressBees</span>
              <span>Acuvate</span>
              <span>Concentrix</span>
              <span>Epsilon</span>
              <span>HCL</span>
              <span>LG Soft</span>
              <span>Tech Mahindra</span>
              <span>Infosys</span>
              <span>Capgemini</span>
              <span>TCS </span>
              <span></span>
            </div>
          </Marquee>
        </div>
      </section>

      <section className="text-center py-12 bg-gradient-to-b from-white to-slate-50">
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">
          Explore Our Courses
        </h2>

        <div className="inline-flex rounded-full overflow-hidden shadow-md">
          <button
            onClick={() => setCourseMode("online")}
            className={`px-8 py-2.5 font-semibold transition-all duration-300 ${courseMode === "online"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            Online
          </button>

          <button
            onClick={() => setCourseMode("offline")}
            className={`px-8 py-2.5 font-semibold transition-all duration-300 ${courseMode === "offline"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            Offline
          </button>
        </div>
      </section>


      {/* Main Course Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-8">
            {/* Sidebar (Desktop) */}
            <aside className="hidden lg:block w-72 bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4 flex items-center justify-between">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <span>🔥</span> Popular Courses
                </h2>
              </div>

              <div className="py-3">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key as CategoryKey)}
                    className={`w-full flex items-center justify-between px-6 py-3.5 text-left text-base transition-all duration-200 ${selectedCategory === cat.key
                      ? "bg-blue-50 text-blue-600 font-semibold border-l-4 border-blue-500"
                      : "text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-lg">{cat.icon}</span>
                      {cat.label}
                    </span>
                    <span className="text-gray-400">›</span>
                  </button>
                ))}
              </div>
            </aside>


            <div className="block lg:hidden w-full mb-8">
              {categories.map((cat) => {
                const isExpanded = expanded === cat.key;
                const categoryCourses =
                  selectedCategory === cat.key
                    ? filteredCourses
                    : filteredCourses.filter((course: any) => course.category === cat.key);

                return (
                  <div
                    key={cat.key}
                    className="border rounded-xl mb-3 bg-white shadow-sm overflow-hidden transition-all duration-300"
                  >
                    <button
                      onClick={() => {
                        setSelectedCategory(cat.key as CategoryKey);
                        setExpanded(isExpanded ? null : cat.key);
                      }}
                      className={`flex justify-between items-center w-full px-5 py-3 text-left font-semibold text-gray-800 ${isExpanded ? "bg-blue-50" : ""
                        }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className="text-lg">{cat.icon}</span>
                        {cat.label}
                      </span>
                      <span
                        className={`text-blue-600 font-bold text-xl transition-transform duration-300 ${isExpanded ? "rotate-180" : ""
                          }`}
                      >
                        {isExpanded ? "−" : "+"}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="p-4 bg-blue-50 border-t border-blue-100">
                        {categoryCourses.length > 0 ? (
                          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {categoryCourses.map((course: any) => (
                              <div
                                key={course.id}
                                className="bg-white rounded-2xl p-6 shadow-lg flex flex-col items-center text-center gap-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-blue-100"
                              >
                                <div
                                  className={`p-4 rounded-2xl bg-gradient-to-br ${course.color} text-white flex items-center justify-center`}
                                >
                                  {course.icon}
                                </div>

                                {/* Title */}
                                <h3 className="font-bold text-lg text-gray-900 leading-snug">
                                  {course.title}
                                </h3>

                                {/* Description */}
                                <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                                  {course.description}
                                </p>

                                {/* Duration */}
                                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                  <Clock size={15} /> {course.isProgramming ? `${course.weeks} weeks` : "6 months"}
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-col gap-3 w-full mt-2">
                                  <Link
                                    href={`/syallabus/${course.id}`}
                                    className="w-full py-2.5 rounded-xl font-semibold text-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                                  >
                                    View Full Syllabus
                                  </Link>
                                  <button
                                    onClick={handleEnroll}
                                    className={`w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r ${course.color} hover:opacity-90 transition`}
                                  >
                                    Enroll Now
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-600 text-center text-sm py-2">
                            Courses coming soon!
                          </p>
                        )}
                      </div>


                    )}
                  </div>
                );
              })}
            </div>



            {/* Course Grid (Desktop) */}
            {/* <div className="hidden lg:block flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {filteredCourses.length === 0 ? (
                  <p className="col-span-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-blue-50 to-white rounded-2xl py-16 px-6 shadow-inner border border-blue-100">
                    <span className="text-4xl mb-4">🎓</span>
                    <span className="text-xl font-semibold text-gray-800 mb-2">
                      Courses are coming soon!
                    </span>
                    <span className="text-gray-600 max-w-md">
                      Stay tuned for exciting new courses — we’re working hard
                      to bring you the best learning experience.
                    </span>
                  </p>
                ) : (
                  filteredCourses.map((course: any) => (
                    <div
                      key={course.id}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden flex flex-col"
                    >
                      <div className={`h-2 bg-gradient-to-r ${course.color}`} />
                      <div className="p-7 flex flex-col justify-between h-full">
                        <div>
                          <div className="flex items-center gap-4 mb-5">
                            <div
                              className={`p-4 rounded-2xl bg-gradient-to-br ${course.color} text-white shadow-md`}
                            >
                              {course.icon}
                            </div>
                            <h3 className="font-extrabold text-xl text-gray-900 leading-tight">
                              {course.title}
                            </h3>
                          </div>
                          <p className="text-gray-700 mb-6 text-sm leading-relaxed line-clamp-3">
                            {course.description}
                          </p>
                        </div>
                        <div className="mt-auto space-y-3">
                          <Link
                            href={`/syallabus/${course.id}`}
                            className="block w-full text-center py-3 rounded-lg font-semibold bg-gray-100 hover:bg-gray-200 text-gray-800 transition"
                          >
                            View Full Syllabus
                          </Link>
                          <button
                            onClick={handleEnroll}
                            className={`w-full py-3 rounded-lg font-bold text-white bg-gradient-to-r ${course.color} hover:opacity-90 shadow-md transition`}
                          >
                            Enroll Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div> */}
            {/* Course Grid (Desktop) */}
            <div className="hidden lg:flex flex-1 h-[80vh] overflow-hidden rounded-2xl bg-white shadow-sm">


              {/* Scrollable Course Area */}
              <div className="flex-1 overflow-y-auto px-8 py-6 bg-gradient-to-br from-blue-50 to-white">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 pb-10">
                  {filteredCourses.length === 0 ? (
                    <p className="col-span-full flex flex-col items-center justify-center text-center bg-white rounded-2xl py-16 px-6 shadow-inner border border-blue-100">
                      <span className="text-4xl mb-4">🎓</span>
                      <span className="text-xl font-semibold text-gray-800 mb-2">
                        Courses are coming soon!
                      </span>
                      <span className="text-gray-600 max-w-md">
                        Stay tuned for exciting new courses — we’re working hard to bring you the best learning experience.
                      </span>
                    </p>
                  ) : (
                    filteredCourses.map((course: any) => (
                      <div
                        key={course.id}
                        className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden flex flex-col"
                      >
                        <div className={`h-2 bg-gradient-to-r ${course.color}`} />
                        <div className="p-7 flex flex-col justify-between h-full">
                          <div>
                            <div className="flex items-center gap-4 mb-5">
                              <div
                                className={`p-4 rounded-2xl bg-gradient-to-br ${course.color} text-white shadow-md`}
                              >
                                {course.icon}
                              </div>
                              <h3 className="font-extrabold text-xl text-gray-900 leading-tight">
                                {course.title}
                              </h3>
                            </div>
                            <p className="text-gray-700 mb-6 text-sm leading-relaxed line-clamp-3">
                              {course.description}
                            </p>
                          </div>
                          <div className="mt-auto space-y-3">
                            <Link
                              href={`/syallabus/${course.id}`}
                              className="block w-full text-center py-3 rounded-lg font-semibold bg-gray-100 hover:bg-gray-200 text-gray-800 transition"
                            >
                              View Full Syllabus
                            </Link>
                            <button
                              onClick={handleEnroll}
                              className={`w-full py-3 rounded-lg font-bold text-white bg-gradient-to-r ${course.color} hover:opacity-90 shadow-md transition`}
                            >
                              Enroll Now
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>


          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-r from-blue-400 via-blue-400 to-indigo-400 py-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url("/blue-wave-bg.png")', // use your new blue background image
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Content */}
        <div className="relative max-w-4xl mx-auto text-center text-white px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-8">
            Take your first step towards mastering in-demand tech skills.
            Connect with us to enroll today.
          </p>
          <button
            onClick={handleEnroll}
            className="inline-block bg-white text-blue-700 px-8 py-3 rounded-lg font-semibold shadow-md hover:bg-blue-50 hover:scale-105 transition-all duration-300"
          >
            Contact Us
          </button>
        </div>

        {/* Bottom light glow */}
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-blue-400/30 to-transparent pointer-events-none" />
      </section>


      <Footer />
    </div>
  );
}